// ============================================================================
// CustomLobby.js — Redesigned
// ----------------------------------------------------------------------------
// Design decisions:
//
// • Five distinct phases, each a clean-cut full-screen view:
//     0) ENTRY HUB     — create lobby, join by code, public lobby browser
//     1) LOBBY SETUP   — arena/poster layout, two team slabs flanking a
//                        central stage (mode/region/map/rules + START)
//     2) PROVISIONING  — full-screen "spinning up server" view
//     3) IN-MATCH      — CS:GO-style tab scoreboard with live per-player stats
//     4) MATCH-OVER    — final score + PLAY AGAIN / DISBAND
//
// • Brand language: Bebas Neue headings (billboard scale), Rajdhani body,
//   diamond motifs as structural dividers, red `#E8192C` as primary signal
//   colour, orange `#FF6B2B` for leader/highlights. All heading periods red.
//
// • Leader vs member: middle-ground distinction. Leader sees active red
//   controls + a "LEADER" chip on their identity. Members see muted controls
//   with `disabled`/locked treatment. Both see the same structural layout —
//   the chrome doesn't swap, just the affordances.
//
// • Team names: click-to-edit inline. Click the team name → it becomes an
//   input → Enter or blur to commit. Lock icon next to the name toggles the
//   global team-names-lock. No modal, no two-step.
//
// • Score bubbles are always visible (even pre-match showing 0–0), per
//   product direction.
//
// • Match Rules collapsed into a single sheet that opens on click. Map
//   shown as a large clickable tile that opens the picker as a sheet.
//
// • All API calls go through existing exports in `src/api/api.js` — no
//   signature changes.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  createCustomLobby,
  getCustomLobby,
  getCurrentCustomLobby,
  joinCustomLobby,
  leaveCustomLobby,
  setLobbyMode,
  setLobbyMap,
  setLobbyRegion,
  assignTeam,
  randomiseTeams,
  promoteToCaptain,
  kickFromLobby,
  startCustomMatch,
  setKnifeRound,
  setTeamLock,
  setAllTalk,
  setTeamName,
  setTeamNamesLock,
  addBots,
  removeBot,
  resetLobby,
  disbandLobby,
  getPublicLobbies,
  setLobbyVisibility,
  inviteFriendToLobby,
  getFriends,
  getMatchStats,
} from '../api/api';

// ─── Brand tokens ──────────────────────────────────────────────────────────
const C = {
  red:       '#E8192C',
  redDeep:   '#A6111E',
  orange:    '#FF6B2B',
  bg:        '#0C0905',
  bgSoft:    '#13100A',
  off:       '#F0F2F5',
  offDim:    'rgba(240,242,245,0.55)',
  offGhost:  'rgba(240,242,245,0.18)',
  card:      '#111111',
  cardHi:    '#171717',
  border:    '#1e1e1e',
  borderHi:  '#2a2a2a',
};

const F = {
  display: "'Bebas Neue', sans-serif",
  body:    "'Rajdhani', sans-serif",
};

const rankSummary = (player = {}) => {
  const rank = player.rank;
  if (rank?.is_ranked) return `${rank.rank_name} · ${rank.elo} ELO`;
  if (rank) return `${rank.mode_label || 'Rank'} placements ${rank.placement_games_played || 0}/${rank.placement_games_required || 0}`;
  return player.elo ? `ELO ${player.elo}` : 'Player';
};

// ─── Static data ───────────────────────────────────────────────────────────

// Base URL for endpoints not exported from api.js. As of latest api.js:
//   GET /custom-lobby/{id}/server-progress  — used during provisioning phase.
// (Stats are now exported as getMatchStats.)
const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://repeaked-backend-production.up.railway.app';

async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

// AuthContext reads responses as `res.data.user`, which means api.js returns
// axios-style { data: ... } envelopes. Unwrap consistently here.
function unwrap(res) {
  if (res == null) return res;
  if (typeof res === 'object' && 'data' in res) return res.data;
  return res;
}

const REGIONS = [
  { id: 'lon1', label: 'London',         flag: '🇬🇧' },
  { id: 'ams3', label: 'Amsterdam',      flag: '🇳🇱' },
  { id: 'nyc1', label: 'New York',       flag: '🇺🇸' },
  { id: 'sfo3', label: 'San Francisco',  flag: '🇺🇸' },
  { id: 'nbg1', label: 'Nuremberg',      flag: '🇩🇪' },
  { id: 'hel1', label: 'Helsinki',       flag: '🇫🇮' },
];

const MAPS_5V5 = [
  { id: 'de_dust2',    name: 'Dust 2',      img: 'dust2'    },
  { id: 'de_mirage',   name: 'Mirage',      img: 'mirage'   },
  { id: 'de_inferno',  name: 'Inferno',     img: 'inferno'  },
  { id: 'de_nuke',     name: 'Nuke',        img: 'nuke'     },
  { id: 'de_overpass', name: 'Overpass',    img: 'overpass' },
  { id: 'de_ancient',  name: 'Ancient',     img: 'ancient'  },
  { id: 'de_anubis',   name: 'Anubis',      img: 'anubis'   },
  { id: 'de_vertigo',  name: 'Vertigo',     img: 'vertigo'  },
  { id: 'de_train',    name: 'Train',       img: 'train'    },
  { id: 'de_cbble',    name: 'Cobblestone', img: 'cbble'    },
  { id: 'de_cache',    name: 'Cache',       img: 'cache'    },
  { id: 'de_tuscan',   name: 'Tuscan',      img: 'tuscan'   },
];

const MAPS_WINGMAN = [
  { id: 'de_shortdust',  name: 'Short Dust',  img: 'shortdust' },
  { id: 'de_lake',       name: 'Lake',        img: 'lake'      },
  { id: 'de_shortnuke',  name: 'Short Nuke',  img: 'nuke'      },
  { id: 'de_inferno',    name: 'Inferno',     img: 'inferno'   },
  { id: 'de_cbble',      name: 'Cobblestone', img: 'cbble'     },
  { id: 'de_overpass',   name: 'Overpass',    img: 'overpass'  },
  { id: 'de_vertigo',    name: 'Vertigo',     img: 'vertigo'   },
  { id: 'de_train',      name: 'Train',       img: 'train'     },
  { id: 'de_canals',     name: 'Canals',      img: 'canals'    },
  { id: 'de_safehouse',  name: 'Safehouse',   img: 'safehouse' },
  { id: 'de_bank',       name: 'Bank',        img: 'bank'      },
  { id: 'de_stmarc',     name: 'St. Marc',    img: 'stmarc'    },
];

const RULE_TOOLTIPS = {
  knife:      'First round is a knife round — winners pick which side they start on.',
  allTalk:    'Both teams can hear each other in voice chat throughout the match.',
  teamLock:   'Lock players to their current team. Prevents swapping mid-lobby.',
  visibility: 'Public lobbies appear in the browser. Private lobbies only join via code.',
};

// ─── Tiny atomic components ────────────────────────────────────────────────

// Diamond — the brand motif used as a structural divider throughout the page.
function Diamond({ size = 14, filled = false, color = C.red }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        transform: 'rotate(45deg)',
        border: `2px solid ${color}`,
        background: filled ? color : 'transparent',
        flexShrink: 0,
      }}
    />
  );
}

// Lock icon — minimal SVG glyph. Outline when unlocked, solid hasp when locked.
function LockIcon({ locked, size = 14, color }) {
  const c = color || (locked ? C.orange : C.offDim);
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      style={{ display: 'block' }}
    >
      {/* Shackle */}
      <path
        d={locked
          ? 'M5 7 V5 a3 3 0 0 1 6 0 V7'
          : 'M5 7 V5 a3 3 0 0 1 5.5 -1.7'}
        stroke={c}
        strokeWidth="1.5"
        strokeLinecap="square"
        fill="none"
      />
      {/* Body */}
      <rect x="3.5" y="7" width="9" height="7" rx="1"
        stroke={c} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

// RedPeriod — the brand's signature "period at the end of every heading".
function H({ children, size = 64, color = C.off, style }) {
  return (
    <h2
      style={{
        fontFamily: F.display,
        fontWeight: 400,
        fontSize: size,
        letterSpacing: '0.02em',
        lineHeight: 0.95,
        color,
        margin: 0,
        ...style,
      }}
    >
      {children}
      <span style={{ color: C.red }}>.</span>
    </h2>
  );
}

// Subtle background field — drifting diamonds, very low opacity.
function DriftField() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const n = 14;
    const seeds = Array.from({ length: n }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.015,
      vy: (Math.random() - 0.5) * 0.015,
      s: 18 + Math.random() * 38,
      o: 0.025 + Math.random() * 0.05,
      r: Math.random() * Math.PI * 2,
    }));
    const nodes = seeds.map((seed) => {
      const d = document.createElement('div');
      d.style.position = 'absolute';
      d.style.width = `${seed.s}px`;
      d.style.height = `${seed.s}px`;
      d.style.transform = `rotate(45deg)`;
      d.style.border = `1px solid ${C.red}`;
      d.style.opacity = String(seed.o);
      el.appendChild(d);
      return d;
    });
    let raf;
    const tick = () => {
      seeds.forEach((s, i) => {
        s.x += s.vx; s.y += s.vy;
        if (s.x < -5) s.x = 105; if (s.x > 105) s.x = -5;
        if (s.y < -5) s.y = 105; if (s.y > 105) s.y = -5;
        nodes[i].style.left = `${s.x}%`;
        nodes[i].style.top  = `${s.y}%`;
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); nodes.forEach((n) => n.remove()); };
  }, []);
  return (
    <div
      ref={ref}
      aria-hidden
      style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        overflow: 'hidden', zIndex: 1,
      }}
    />
  );
}

// Generic button — primary (red), secondary (outline), ghost (text).
function Btn({
  variant = 'primary',
  size = 'md',
  disabled,
  onClick,
  children,
  style,
  title,
  type = 'button',
}) {
  const [hover, setHover] = useState(false);
  const sizes = {
    sm: { padding: '8px 14px',  fontSize: 13, height: 34 },
    md: { padding: '12px 22px', fontSize: 15, height: 44 },
    lg: { padding: '18px 36px', fontSize: 20, height: 60 },
    xl: { padding: '22px 56px', fontSize: 28, height: 76 },
  };
  const base = {
    fontFamily: F.display,
    fontWeight: 400,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none',
    transition: 'transform 120ms ease, background 120ms ease, color 120ms ease',
    transform: hover && !disabled ? 'translateY(-1px)' : 'translateY(0)',
    opacity: disabled ? 0.45 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    ...sizes[size],
  };
  const variants = {
    primary: {
      background: disabled ? C.cardHi : (hover ? C.red : C.red),
      color: C.off,
      boxShadow: hover && !disabled
        ? `0 0 0 3px rgba(232,25,44,0.18), 0 8px 24px rgba(232,25,44,0.35)`
        : `0 4px 16px rgba(232,25,44,0.25)`,
    },
    secondary: {
      background: 'transparent',
      color: hover ? C.off : C.off,
      borderTop:    `1px solid ${hover ? C.red : C.border}`,
      borderRight:  `1px solid ${hover ? C.red : C.border}`,
      borderBottom: `1px solid ${hover ? C.red : C.border}`,
      borderLeft:   `1px solid ${hover ? C.red : C.border}`,
    },
    ghost: {
      background: 'transparent',
      color: hover ? C.red : C.offDim,
    },
    danger: {
      background: 'transparent',
      color: hover ? C.red : C.offDim,
      borderTop:    `1px solid ${hover ? C.red : C.border}`,
      borderRight:  `1px solid ${hover ? C.red : C.border}`,
      borderBottom: `1px solid ${hover ? C.red : C.border}`,
      borderLeft:   `1px solid ${hover ? C.red : C.border}`,
    },
  };
  return (
    <button
      type={type}
      disabled={disabled}
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...base, ...variants[variant], ...style }}
    >
      {children}
    </button>
  );
}

// Sheet — slide-up modal used for map picker, match rules, friend invite list.
function Sheet({ open, onClose, title, children, width = 880 }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.74)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'sheetFade 160ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: `min(${width}px, 92vw)`,
          maxHeight: '86vh',
          overflow: 'auto',
          background: C.card,
          borderTop:    `1px solid ${C.borderHi}`,
          borderRight:  `1px solid ${C.borderHi}`,
          borderBottom: `1px solid ${C.borderHi}`,
          borderLeft:   `1px solid ${C.borderHi}`,
          boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
          animation: 'sheetUp 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          position: 'relative',
        }}
      >
        {/* corner diamonds */}
        <div style={{ position: 'absolute', top: -7, left: -7 }}><Diamond size={12} filled /></div>
        <div style={{ position: 'absolute', top: -7, right: -7 }}><Diamond size={12} /></div>
        <div style={{ position: 'absolute', bottom: -7, left: -7 }}><Diamond size={12} /></div>
        <div style={{ position: 'absolute', bottom: -7, right: -7 }}><Diamond size={12} filled /></div>

        <div style={{
          padding: '24px 32px 16px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <H size={36}>{title}</H>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.offDim, fontSize: 28, fontFamily: F.display,
              padding: 4,
            }}
            aria-label="Close"
          >×</button>
        </div>
        <div style={{ padding: 32 }}>{children}</div>
      </div>
    </div>
  );
}

// Tooltip — hover info `i` button used on rules.
function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ position: 'relative', display: 'inline-flex', marginLeft: 8 }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: '50%',
        borderTop:    `1px solid ${C.offGhost}`,
        borderRight:  `1px solid ${C.offGhost}`,
        borderBottom: `1px solid ${C.offGhost}`,
        borderLeft:   `1px solid ${C.offGhost}`,
        color: C.offDim,
        fontFamily: F.body, fontSize: 11, lineHeight: '14px', textAlign: 'center',
        cursor: 'help', fontWeight: 600,
      }}>i</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)',
          background: C.bgSoft,
          borderTop:    `1px solid ${C.borderHi}`,
          borderRight:  `1px solid ${C.borderHi}`,
          borderBottom: `1px solid ${C.borderHi}`,
          borderLeft:   `1px solid ${C.borderHi}`,
          color: C.off, fontFamily: F.body, fontSize: 12,
          padding: '8px 12px', whiteSpace: 'nowrap', zIndex: 50,
          pointerEvents: 'none',
        }}>{text}</span>
      )}
    </span>
  );
}

// ─── Phase 0: ENTRY HUB ────────────────────────────────────────────────────
function EntryHub({ onCreate, onJoinCode, publicLobbies, onJoinPublic, loadingPublic, refreshPublic }) {
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState('');
  const [browseOpen, setBrowseOpen] = useState(false);

  const doCreate = async () => {
    setErr(''); setCreating(true);
    try { await onCreate(); }
    catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not create lobby';
      setErr(detail);
    }
    setCreating(false);
  };
  const doJoin = async () => {
    if (!code.trim()) return;
    setErr(''); setJoining(true);
    try { await onJoinCode(code.trim().toUpperCase()); }
    catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Invalid code';
      setErr(detail);
    }
    setJoining(false);
  };

  const renderPublicLobbyRows = (lobbies) => lobbies.map((lb) => {
    const region = REGIONS.find((r) => r.id === lb.region);
    const max = lb.mode === 'wingman' ? 4 : 10;
    const count = (lb.players?.length || 0);
    return (
      <PublicLobbyRow
        key={lb.id || lb.code}
        code={lb.code}
        host={lb.leader_name || lb.host || '—'}
        mode={lb.mode === 'wingman' ? 'Wingman' : '5v5'}
        region={region ? `${region.flag} ${region.label}` : lb.region}
        count={count} max={max}
        onJoin={() => onJoinPublic(lb.code)}
      />
    );
  });
  const renderPublicLobbyTableHead = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '90px 1fr 110px 130px 140px 120px',
      padding: '14px 24px',
      borderBottom: `1px solid ${C.border}`,
      fontFamily: F.body, fontSize: 11, letterSpacing: '0.25em',
      textTransform: 'uppercase', color: C.offDim,
    }}>
      <div>Code</div>
      <div>Host</div>
      <div>Mode</div>
      <div>Region</div>
      <div>Players</div>
      <div style={{ textAlign: 'right' }}>Action</div>
    </div>
  );

  return (
    <div style={{
      position: 'relative', zIndex: 2,
      maxWidth: 1280, margin: '0 auto',
      padding: 'clamp(16px, 3dvh, 48px) 32px clamp(16px, 3dvh, 48px)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
        <Diamond size={18} filled />
        <span style={{
          fontFamily: F.body, fontSize: 13, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: C.offDim,
        }}>Custom Match</span>
      </div>
      <H size="clamp(62px, 11dvh, 120px)" style={{ marginBottom: 6 }}>Pick your fight</H>
      <p style={{
        fontFamily: F.body, color: C.offDim, fontSize: 18,
        margin: 0, marginBottom: 'clamp(20px, 3.5dvh, 44px)', maxWidth: 640,
      }}>
        Spin up a private server, gather your friends, fill the rest with bots.
        You set the rules.
      </p>

      {/* Top action row: Create / Join */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 32,
        marginBottom: 'clamp(16px, 3dvh, 32px)',
      }}>
        {/* Create card */}
        <div style={{
          background: `linear-gradient(135deg, ${C.card} 0%, ${C.bgSoft} 100%)`,
          borderTop:    `1px solid ${C.borderHi}`,
          borderRight:  `1px solid ${C.borderHi}`,
          borderBottom: `2px solid ${C.red}`,
          borderLeft:   `1px solid ${C.borderHi}`,
          padding: 'clamp(22px, 3.5dvh, 40px) 40px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', right: -40, top: -40, width: 180, height: 180,
            transform: 'rotate(45deg)',
            borderTop:    `1px solid ${C.red}`,
            borderRight:  `1px solid ${C.red}`,
            borderBottom: `1px solid ${C.red}`,
            borderLeft:   `1px solid ${C.red}`,
            opacity: 0.12,
          }} />
          <div style={{
            fontFamily: F.body, fontSize: 12, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: C.red, marginBottom: 'clamp(6px, 1.5dvh, 12px)',
          }}>01 — Host</div>
          <H size="clamp(34px, 6dvh, 56px)" style={{ marginBottom: 8 }}>Create lobby</H>
          <p style={{
            fontFamily: F.body, color: C.offDim, fontSize: 'clamp(13px, 1.8dvh, 15px)',
            margin: 0, marginBottom: 'clamp(12px, 2.5dvh, 28px)', maxWidth: 440,
          }}>
            Generate a fresh 6-character code. Invite friends, configure the
            match, and start the server when you're ready.
          </p>
          <Btn
            variant="primary"
            size="lg"
            onClick={doCreate}
            disabled={creating}
            style={{ height: 'clamp(46px, 7dvh, 60px)', padding: '0 36px' }}
          >
            {creating ? 'Creating…' : 'Create Lobby →'}
          </Btn>
        </div>

        {/* Join card */}
        <div style={{
          background: C.card,
          borderTop:    `1px solid ${C.border}`,
          borderRight:  `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          borderLeft:   `1px solid ${C.border}`,
          padding: 'clamp(22px, 3.5dvh, 40px) 40px',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            fontFamily: F.body, fontSize: 12, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: C.orange, marginBottom: 'clamp(6px, 1.5dvh, 12px)',
          }}>02 — Join</div>
          <H size="clamp(34px, 6dvh, 56px)" style={{ marginBottom: 8 }}>Got a code</H>
          <p style={{
            fontFamily: F.body, color: C.offDim, fontSize: 'clamp(13px, 1.8dvh, 15px)',
            margin: 0, marginBottom: 'clamp(12px, 2.5dvh, 24px)',
          }}>
            Enter the 6-character lobby code your friend sent you.
          </p>

          <div style={{ display: 'flex', gap: 12 }}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && doJoin()}
              placeholder="ABCDEF"
              maxLength={6}
              style={{
                flex: 1, height: 'clamp(46px, 7dvh, 60px)', padding: '0 20px',
                fontFamily: F.display, fontSize: 'clamp(22px, 4dvh, 28px)', letterSpacing: '0.4em',
                textAlign: 'center',
                background: C.bg, color: C.off,
                borderTop:    `1px solid ${C.borderHi}`,
                borderRight:  `1px solid ${C.borderHi}`,
                borderBottom: `1px solid ${C.borderHi}`,
                borderLeft:   `1px solid ${C.borderHi}`,
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderTopColor = C.orange;
                e.target.style.borderRightColor = C.orange;
                e.target.style.borderBottomColor = C.orange;
                e.target.style.borderLeftColor = C.orange;
              }}
              onBlur={(e) => {
                e.target.style.borderTopColor = C.borderHi;
                e.target.style.borderRightColor = C.borderHi;
                e.target.style.borderBottomColor = C.borderHi;
                e.target.style.borderLeftColor = C.borderHi;
              }}
            />
            <Btn
              variant="secondary"
              size="lg"
              onClick={doJoin}
              disabled={joining || !code.trim()}
              style={{ height: 'clamp(46px, 7dvh, 60px)', padding: '0 36px' }}
            >
              {joining ? '…' : 'Join'}
            </Btn>
          </div>
          {err && (
            <div style={{
              marginTop: 16, fontFamily: F.body, fontSize: 13, color: C.red,
            }}>{err}</div>
          )}
        </div>
      </div>

      {/* Public lobby browser */}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 'clamp(10px, 2dvh, 18px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <H size="clamp(34px, 5dvh, 48px)">Public lobbies</H>
          <span style={{
            fontFamily: F.body, fontSize: 13, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: C.offDim,
          }}>{publicLobbies.length} live</span>
        </div>
        <Btn variant="ghost" size="sm" onClick={refreshPublic}>
          {loadingPublic ? 'Refreshing…' : '↻ Refresh'}
        </Btn>
      </div>

      <div style={{
        borderTop:    `1px solid ${C.borderHi}`,
        borderRight:  `1px solid ${C.borderHi}`,
        borderBottom: `2px solid ${C.red}`,
        borderLeft:   `1px solid ${C.borderHi}`,
        background: `linear-gradient(135deg, ${C.card} 0%, ${C.bgSoft} 100%)`,
        padding: 'clamp(20px, 3.2dvh, 36px) 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 28,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', right: -34, bottom: -52, width: 170, height: 170,
          transform: 'rotate(45deg)',
          borderTop:    `1px solid ${C.red}`,
          borderRight:  `1px solid ${C.red}`,
          borderBottom: `1px solid ${C.red}`,
          borderLeft:   `1px solid ${C.red}`,
          opacity: 0.1,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: F.body, fontSize: 12, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: C.red, marginBottom: 10,
          }}>{publicLobbies.length} live public {publicLobbies.length === 1 ? 'lobby' : 'lobbies'}</div>
          <H size="clamp(34px, 5dvh, 48px)" style={{ marginBottom: 6 }}>Find a match</H>
          <p style={{
            margin: 0, fontFamily: F.body, color: C.offDim, fontSize: 'clamp(13px, 1.8dvh, 15px)',
            maxWidth: 540,
          }}>
            Open the full public lobby browser, scroll through live rooms, and join from the list.
          </p>
        </div>
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
        }}>
          <Btn
            variant="primary"
            size="lg"
            onClick={() => setBrowseOpen(true)}
            style={{ height: 'clamp(48px, 7dvh, 60px)', padding: '0 36px' }}
          >
            Browse public lobbies
          </Btn>
          <Btn variant="ghost" size="sm" onClick={refreshPublic}>
            {loadingPublic ? 'Refreshing…' : '↻'}
          </Btn>
        </div>
      </div>

      <Sheet open={browseOpen} onClose={() => setBrowseOpen(false)} title="Browse public lobbies" width={980}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, marginBottom: 18,
        }}>
          <p style={{
            margin: 0, fontFamily: F.body, color: C.offDim, fontSize: 15,
            maxWidth: 560,
          }}>
            Scroll through every live public lobby and jump straight into the fight.
          </p>
          <Btn variant="ghost" size="sm" onClick={refreshPublic}>
            {loadingPublic ? 'Refreshing…' : '↻ Refresh'}
          </Btn>
        </div>

        <div style={{
          borderTop:    `1px solid ${C.borderHi}`,
          borderRight:  `1px solid ${C.borderHi}`,
          borderBottom: `1px solid ${C.borderHi}`,
          borderLeft:   `1px solid ${C.borderHi}`,
          background: C.bgSoft,
          maxHeight: '56vh',
          overflowY: 'auto',
          overflowX: 'auto',
        }}>
          <div style={{ minWidth: 820 }}>
            {renderPublicLobbyTableHead()}
            {publicLobbies.length === 0 ? (
              <div style={{
                padding: '48px 24px', textAlign: 'center',
                fontFamily: F.body, color: C.offDim, fontSize: 14,
              }}>
                No public lobbies right now. Be the first.
              </div>
            ) : renderPublicLobbyRows(publicLobbies)}
          </div>
        </div>
      </Sheet>
    </div>
  );
}

function PublicLobbyRow({ code, host, mode, region, count, max, onJoin }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 110px 130px 140px 120px',
        padding: '18px 24px', alignItems: 'center',
        borderBottom: `1px solid ${C.border}`,
        background: hover ? C.cardHi : 'transparent',
        transition: 'background 120ms ease',
      }}
    >
      <div style={{
        fontFamily: F.display, fontSize: 22, letterSpacing: '0.15em',
        color: hover ? C.red : C.off,
      }}>{code}</div>
      <div style={{ fontFamily: F.body, fontSize: 15, color: C.off }}>{host}</div>
      <div style={{ fontFamily: F.body, fontSize: 13, color: C.offDim, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{mode}</div>
      <div style={{ fontFamily: F.body, fontSize: 13, color: C.offDim }}>{region}</div>
      <div style={{ fontFamily: F.display, fontSize: 18, color: C.off, letterSpacing: '0.05em' }}>
        {count}<span style={{ color: C.offDim }}> / {max}</span>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Btn variant={hover ? 'primary' : 'secondary'} size="sm" onClick={onJoin}>Join</Btn>
      </div>
    </div>
  );
}

// ─── Phase 1: LOBBY SETUP ──────────────────────────────────────────────────

// MapTile — large clickable current-map display.
function MapTile({ mapId, mode, onClick, disabled }) {
  const list = mode === 'wingman' ? MAPS_WINGMAN : MAPS_5V5;
  const map = list.find((m) => m.id === mapId) || list[0];
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', width: '100%', aspectRatio: '21 / 9',
        background: C.bg,
        borderTop:    `1px solid ${hover && !disabled ? C.red : C.borderHi}`,
        borderRight:  `1px solid ${hover && !disabled ? C.red : C.borderHi}`,
        borderBottom: `1px solid ${hover && !disabled ? C.red : C.borderHi}`,
        borderLeft:   `1px solid ${hover && !disabled ? C.red : C.borderHi}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        overflow: 'hidden', padding: 0,
        transition: 'border-color 120ms ease',
      }}
    >
      <div
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(/assets/maps/${map.img}.jpg)`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: hover && !disabled ? 'brightness(0.7) saturate(1.1)' : 'brightness(0.55)',
          transition: 'filter 220ms ease, transform 400ms ease',
          transform: hover && !disabled ? 'scale(1.04)' : 'scale(1)',
        }}
      />
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(180deg, transparent 30%, rgba(12,9,5,0.92) 100%)`,
      }} />
      <div style={{
        position: 'absolute', top: 10, left: 12,
        fontFamily: F.body, fontSize: 9, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: C.offDim,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Diamond size={6} filled /> Map
      </div>
      <div style={{
        position: 'absolute', bottom: 10, left: 14, right: 14,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{
            fontFamily: F.display, fontSize: 32, color: C.off,
            letterSpacing: '0.02em', lineHeight: 0.9,
          }}>
            {map.name}<span style={{ color: C.red }}>.</span>
          </div>
          <div style={{
            fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: C.offDim, marginTop: 3,
          }}>{map.id}</div>
        </div>
        {!disabled && (
          <div style={{
            fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: hover ? C.red : C.offDim,
            transition: 'color 120ms ease',
          }}>
            Change →
          </div>
        )}
      </div>
    </button>
  );
}

// Map picker grid inside a sheet.
function MapPicker({ mode, current, onPick }) {
  const list = mode === 'wingman' ? MAPS_WINGMAN : MAPS_5V5;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
    }}>
      {list.map((m) => {
        const selected = m.id === current;
        return (
          <MapPickerCell key={m.id} map={m} selected={selected} onClick={() => onPick(m.id)} />
        );
      })}
    </div>
  );
}

function MapPickerCell({ map, selected, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', aspectRatio: '4 / 3',
        background: C.bg, padding: 0,
        borderTop:    `2px solid ${selected ? C.red : (hover ? C.borderHi : C.border)}`,
        borderRight:  `2px solid ${selected ? C.red : (hover ? C.borderHi : C.border)}`,
        borderBottom: `2px solid ${selected ? C.red : (hover ? C.borderHi : C.border)}`,
        borderLeft:   `2px solid ${selected ? C.red : (hover ? C.borderHi : C.border)}`,
        cursor: 'pointer', overflow: 'hidden',
        transition: 'border-color 120ms ease',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(/assets/maps/${map.img}.jpg)`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        filter: selected ? 'brightness(0.8)' : (hover ? 'brightness(0.6)' : 'brightness(0.45)'),
        transition: 'filter 200ms ease',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(180deg, transparent 50%, rgba(12,9,5,0.9) 100%)`,
      }} />
      {selected && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: C.off, fontWeight: 600,
          background: C.red, padding: '4px 8px',
        }}>Selected</div>
      )}
      <div style={{
        position: 'absolute', bottom: 12, left: 14, right: 14,
        fontFamily: F.display, fontSize: 24, color: C.off,
        letterSpacing: '0.04em', textAlign: 'left',
      }}>{map.name}</div>
    </button>
  );
}

// Editable team name — click to edit inline, Enter/blur to commit.
function EditableTeamName({ name, locked, canEdit, onCommit, accent }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const ref = useRef(null);

  useEffect(() => { if (!editing) setValue(name); }, [name, editing]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);

  const commit = () => {
    const trimmed = value.trim().slice(0, 18);
    if (trimmed && trimmed !== name) onCommit(trimmed);
    else setValue(name);
    setEditing(false);
  };
  const cancel = () => { setValue(name); setEditing(false); };

  if (!editing) {
    return (
      <button
        type="button"
        disabled={!canEdit || locked}
        onClick={() => setEditing(true)}
        title={!canEdit ? 'Only the leader can rename teams' : (locked ? 'Team names are locked' : 'Click to edit')}
        style={{
          background: 'transparent', border: 'none', padding: 0, margin: 0,
          cursor: (canEdit && !locked) ? 'text' : 'default',
          fontFamily: F.display, fontSize: 44, letterSpacing: '0.04em',
          color: C.off, textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <span>{name}<span style={{ color: accent }}>.</span></span>
      </button>
    );
  }

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value.slice(0, 18))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') cancel();
      }}
      onBlur={commit}
      maxLength={18}
      style={{
        fontFamily: F.display, fontSize: 44, letterSpacing: '0.04em',
        color: C.off, background: C.bg,
        borderTop: 'none', borderRight: 'none', borderLeft: 'none',
        borderBottom: `2px solid ${accent}`,
        outline: 'none', padding: '2px 6px',
        width: '100%', maxWidth: 320,
      }}
    />
  );
}

// Player / bot card inside a team slab.
function SlotCard({
  slot, team, isLeader, canManage, onMenu, lobbyLeaderId, onJoinTeam, myCurrentTeam,
  // Drag-and-drop (leader-only). Optional.
  draggable, onDragStart, onDragEnd, isDragging,
}) {
  // slot: { kind: 'player' | 'bot' | 'empty', user, bot, idx }
  const [hover, setHover] = useState(false);
  const accent = team === 1 ? C.red : C.orange;

  if (slot.kind === 'empty') {
    const isOnThisTeam = myCurrentTeam === team;
    const canJoin = !isOnThisTeam && !!onJoinTeam;
    return (
      <button
        type="button"
        onClick={canJoin ? () => onJoinTeam(team) : undefined}
        disabled={!canJoin}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          padding: '14px 18px',
          background: hover && canJoin ? `rgba(${team === 1 ? '232,25,44' : '255,107,43'},0.08)` : 'transparent',
          borderTop:    `1px dashed ${hover && canJoin ? accent : C.border}`,
          borderRight:  `1px dashed ${hover && canJoin ? accent : C.border}`,
          borderBottom: `1px dashed ${hover && canJoin ? accent : C.border}`,
          borderLeft:   `1px dashed ${hover && canJoin ? accent : C.border}`,
          display: 'flex', alignItems: 'center', gap: 14,
          color: hover && canJoin ? accent : C.offGhost,
          fontFamily: F.body, fontSize: 13,
          letterSpacing: '0.15em', textTransform: 'uppercase',
          cursor: canJoin ? 'pointer' : 'default',
          width: '100%', textAlign: 'left',
          transition: 'all 120ms ease',
        }}
      >
        <span style={{ fontSize: 18 }}>+</span>
        {canJoin ? (hover ? 'Join this team' : 'Empty slot') : 'Empty slot'}
      </button>
    );
  }

  if (slot.kind === 'bot') {
    return (
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          padding: '12px 18px',
          background: hover ? C.cardHi : C.card,
          borderTop:    `1px solid ${C.border}`,
          borderRight:  `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          borderLeft:   `3px solid ${C.offGhost}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          transition: 'background 120ms ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 36, height: 36, background: C.bg,
            borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
            borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
            color: C.offDim, textTransform: 'uppercase',
          }}>Bot</div>
          <div>
            <div style={{ fontFamily: F.display, fontSize: 18, color: C.off, letterSpacing: '0.02em' }}>
              {slot.bot.bot_name}
            </div>
            <div style={{ fontFamily: F.body, fontSize: 11, color: C.offDim, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              AI Player
            </div>
          </div>
        </div>
        {canManage && (
          <button
            onClick={(e) => onMenu({ kind: 'bot', bot: slot.bot, team }, e)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.offDim, fontSize: 18, padding: 4,
            }}
            title="Remove bot"
          >×</button>
        )}
      </div>
    );
  }

  // player
  const u = slot.user;
  const isLobbyLeader = u.user_id === lobbyLeaderId;
  const canDrag = !!draggable && !!onDragStart;
  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', u.user_id || u.id || '');
        onDragStart(u);
      } : undefined}
      onDragEnd={canDrag ? () => onDragEnd && onDragEnd() : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '14px 18px',
        background: hover ? C.cardHi : C.card,
        borderTop:    `1px solid ${C.border}`,
        borderRight:  `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        borderLeft:   `3px solid ${accent}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        transition: 'background 120ms ease, opacity 120ms ease',
        cursor: canDrag ? 'grab' : 'default',
        opacity: isDragging ? 0.4 : 1,
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        <div style={{
          width: 40, height: 40, background: C.bg,
          backgroundImage: u.avatar_url ? `url(${u.avatar_url})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
          flexShrink: 0,
        }} />
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: F.display, fontSize: 18, color: C.off, letterSpacing: '0.02em',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.username}</span>
            {isLobbyLeader && (
              <span style={{
                fontFamily: F.body, fontSize: 9, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: C.orange,
                padding: '2px 6px',
                borderTop: `1px solid ${C.orange}`, borderRight: `1px solid ${C.orange}`,
                borderBottom: `1px solid ${C.orange}`, borderLeft: `1px solid ${C.orange}`,
              }}>Leader</span>
            )}
          </div>
          <div style={{ fontFamily: F.body, fontSize: 11, color: C.offDim, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            {rankSummary(u)}
          </div>
        </div>
      </div>
      {canManage && (
        <button
          onClick={(e) => onMenu({ kind: 'player', user: u, team }, e)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: C.offDim, fontSize: 20, padding: 4, fontFamily: F.display,
          }}
          title="Manage"
        >⋮</button>
      )}
    </div>
  );
}

// Team slab — one of the two opposing panels.
function TeamSlab({
  team, side, lobby, players, bots, maxSize, canManage, isLeader, lobbyLeaderId,
  onRenameTeam, onTeamNamesLockToggle, onSlotMenu, onAddBot,
  onJoinTeam, myCurrentTeam,
  // Drag-and-drop
  dragging, isDropTarget, onDropPlayer, onDragOver, onDragLeave,
  onPlayerDragStart, onPlayerDragEnd,
}) {
  const accent = team === 1 ? C.red : C.orange;
  const teamName = team === 1 ? (lobby.team1_name || 'Team Alpha') : (lobby.team2_name || 'Team Bravo');
  const score = team === 1 ? (lobby.team1_score || 0) : (lobby.team2_score || 0);
  const namesLocked = !!lobby.team_names_locked;

  // Build ordered slot list: players first, then bots, then empties up to max.
  const slots = [];
  players.forEach((p) => slots.push({ kind: 'player', user: p }));
  bots.forEach((b) => slots.push({ kind: 'bot', bot: b }));
  while (slots.length < maxSize) slots.push({ kind: 'empty' });

  const isActiveDrop = dragging && isDropTarget;

  return (
    <div
      onDragOver={isLeader && dragging ? (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver && onDragOver(team);
      } : undefined}
      onDragLeave={isLeader && dragging ? (e) => {
        // Only fire leave if we've actually left the slab (not entered a child)
        if (e.currentTarget.contains(e.relatedTarget)) return;
        onDragLeave && onDragLeave(team);
      } : undefined}
      onDrop={isLeader && dragging ? (e) => {
        e.preventDefault();
        const userId = e.dataTransfer.getData('text/plain');
        onDropPlayer && onDropPlayer(userId, team);
      } : undefined}
      style={{
        background: `linear-gradient(${side === 'left' ? '135deg' : '225deg'}, ${C.card} 0%, ${C.bgSoft} 100%)`,
        borderTop:    `1px solid ${isActiveDrop ? accent : C.border}`,
        borderRight:  `1px solid ${isActiveDrop ? accent : C.border}`,
        borderBottom: `2px solid ${accent}`,
        borderLeft:   `1px solid ${isActiveDrop ? accent : C.border}`,
        padding: '22px 26px', position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: isActiveDrop
          ? `0 0 0 2px ${accent}, 0 0 32px ${team === 1 ? 'rgba(232,25,44,0.25)' : 'rgba(255,107,43,0.25)'}`
          : 'none',
        transition: 'box-shadow 160ms ease, border-color 160ms ease',
      }}
    >
      {/* Faint diamond watermark */}
      <div aria-hidden style={{
        position: 'absolute', [side === 'left' ? 'left' : 'right']: -60, top: -60,
        width: 220, height: 220, transform: 'rotate(45deg)',
        borderTop: `1px solid ${accent}`, borderRight: `1px solid ${accent}`,
        borderBottom: `1px solid ${accent}`, borderLeft: `1px solid ${accent}`,
        opacity: 0.1,
      }} />

      {/* Header: tag + name + score bubble */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 18, position: 'relative', zIndex: 1,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
            fontFamily: F.body, fontSize: 11, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: accent,
          }}>
            <Diamond size={8} filled color={accent} />
            Team {team === 1 ? 'One' : 'Two'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <EditableTeamName
              name={teamName}
              locked={namesLocked}
              canEdit={isLeader}
              accent={accent}
              onCommit={(newName) => onRenameTeam(team, newName)}
            />
            {isLeader && (
              <button
                onClick={onTeamNamesLockToggle}
                title={namesLocked ? 'Unlock team names' : 'Lock team names'}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: namesLocked ? 1 : 0.6,
                  transition: 'opacity 120ms ease',
                }}
              >
                <LockIcon locked={namesLocked} size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Score bubble — diamond, always visible. Mirrors the logo motif. */}
        <div style={{
          position: 'relative',
          width: 88, height: 88,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {/* Rotated diamond background */}
          <div style={{
            position: 'absolute', inset: 0,
            transform: 'rotate(45deg)',
            background: 'rgba(0,0,0,0.45)',
            borderTop: `2px solid ${accent}`,
            borderRight: `2px solid ${accent}`,
            borderBottom: `2px solid ${accent}`,
            borderLeft: `2px solid ${accent}`,
          }} />
          {/* Inner small dot — completes the logo language */}
          <div style={{
            position: 'absolute',
            width: 6, height: 6, background: accent,
            transform: 'rotate(45deg)',
            top: 8, left: '50%', marginLeft: -3,
            opacity: 0.7,
          }} />
          {/* Score number — sits upright in the centre */}
          <div style={{
            position: 'relative', zIndex: 1,
            fontFamily: F.display, fontSize: 44, color: accent,
            lineHeight: 1, letterSpacing: 0,
            textShadow: '0 2px 8px rgba(0,0,0,0.6)',
          }}>{score}</div>
        </div>
      </div>

      {/* Slot list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
        {slots.map((slot, i) => {
          const isThisDragging = slot.kind === 'player' && dragging
            && (dragging.user_id || dragging.id) === (slot.user?.user_id || slot.user?.id);
          return (
            <SlotCard
              key={i}
              slot={slot}
              team={team}
              isLeader={isLeader}
              canManage={isLeader}
              onMenu={onSlotMenu}
              lobbyLeaderId={lobbyLeaderId}
              onJoinTeam={onJoinTeam}
              myCurrentTeam={myCurrentTeam}
              draggable={isLeader && slot.kind === 'player'}
              onDragStart={onPlayerDragStart}
              onDragEnd={onPlayerDragEnd}
              isDragging={isThisDragging}
            />
          );
        })}
      </div>

      {/* Add bot footer */}
      {isLeader && (
        <div style={{ marginTop: 18 }}>
          <Btn variant="ghost" size="sm" onClick={() => onAddBot(team)}>
            + Add Bot to Team {team === 1 ? 'One' : 'Two'}
          </Btn>
        </div>
      )}
    </div>
  );
}

// Unassigned bench — pinned to the top of the arena, always visible.
// 5 columns × 2 rows = 10 slots, matching max 5v5 capacity. Empty slots
// render as dashed placeholders. Acts as both a status display and a drop
// zone for leaders who want to park players before assigning them.
function UnassignedZone({
  players, isLeader, myUserId,
  dragging, isDropTarget, onDropPlayer, onDragOver, onDragLeave,
  onPlayerDragStart, onPlayerDragEnd, onJoinTeam,
}) {
  const isActive = !!dragging && isDropTarget;
  const SLOTS = 10;

  // Build a stable 10-cell array: filled slots first, then empties.
  const cells = [];
  players.slice(0, SLOTS).forEach((p) => cells.push({ kind: 'player', player: p }));
  while (cells.length < SLOTS) cells.push({ kind: 'empty' });

  return (
    <div
      onDragOver={isLeader && dragging ? (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver && onDragOver('unassigned');
      } : undefined}
      onDragLeave={isLeader && dragging ? (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        onDragLeave && onDragLeave('unassigned');
      } : undefined}
      onDrop={isLeader && dragging ? (e) => {
        e.preventDefault();
        const userId = e.dataTransfer.getData('text/plain');
        onDropPlayer && onDropPlayer(userId, 'unassigned');
      } : undefined}
      style={{
        background: isActive
          ? 'linear-gradient(180deg, rgba(240,242,245,0.04) 0%, rgba(240,242,245,0.02) 100%)'
          : C.card,
        borderTop:    `1px solid ${isActive ? C.off : C.borderHi}`,
        borderRight:  `1px solid ${isActive ? C.off : C.borderHi}`,
        borderBottom: `1px solid ${isActive ? C.off : C.borderHi}`,
        borderLeft:   `1px solid ${isActive ? C.off : C.borderHi}`,
        padding: '10px 14px',
        position: 'relative',
        transition: 'all 160ms ease',
        boxShadow: isActive ? '0 0 0 2px rgba(240,242,245,0.4)' : 'none',
        width: '100%',
      }}
    >
      {/* Header strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: F.body, fontSize: 10, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: C.offDim,
        }}>
          <Diamond size={6} />
          Bench
          <span style={{
            padding: '1px 8px',
            background: C.bg,
            fontFamily: F.display, fontSize: 12, letterSpacing: '0.1em',
            color: C.off,
          }}>{players.length}</span>
        </div>
        <div style={{
          fontFamily: F.body, fontSize: 9, color: C.offGhost,
          letterSpacing: '0.2em', textTransform: 'uppercase',
        }}>
          {isActive
            ? 'Drop to unassign'
            : (isLeader ? 'Drag players to balance teams' : 'Unassigned players')}
        </div>
      </div>

      {/* Fixed grid: 5 cols × 2 rows */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridAutoRows: '44px',
        gap: 6,
      }}>
        {cells.map((cell, i) => {
          if (cell.kind === 'empty') {
            return (
              <div key={`empty-${i}`} style={{
                background: 'transparent',
                borderTop:    `1px dashed ${C.border}`,
                borderRight:  `1px dashed ${C.border}`,
                borderBottom: `1px dashed ${C.border}`,
                borderLeft:   `1px dashed ${C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: F.body, fontSize: 11, color: C.offGhost,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                opacity: 0.5,
              }}>—</div>
            );
          }
          const p = cell.player;
          return (
            <BenchSlot
              key={p.user_id || p.id || i}
              player={p}
              isMe={(p.user_id || p.id) === myUserId}
              draggable={isLeader}
              onDragStart={onPlayerDragStart}
              onDragEnd={onPlayerDragEnd}
              isDragging={dragging && (dragging.user_id || dragging.id) === (p.user_id || p.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

// A single bench cell — fixed-size, matches grid row height.
function BenchSlot({ player, isMe, draggable, onDragStart, onDragEnd, isDragging }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', player.user_id || player.id || '');
        onDragStart && onDragStart(player);
      } : undefined}
      onDragEnd={draggable ? () => onDragEnd && onDragEnd() : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 8px 4px 5px',
        background: hover ? C.cardHi : C.bg,
        borderTop:    `1px solid ${isMe ? C.red : C.borderHi}`,
        borderRight:  `1px solid ${isMe ? C.red : C.borderHi}`,
        borderBottom: `1px solid ${isMe ? C.red : C.borderHi}`,
        borderLeft:   `3px solid ${isMe ? C.red : C.borderHi}`,
        cursor: draggable ? 'grab' : 'default',
        opacity: isDragging ? 0.4 : 1,
        userSelect: 'none',
        transition: 'background 120ms ease, opacity 120ms ease, transform 120ms ease',
        transform: hover && draggable ? 'translateY(-1px)' : 'none',
        minWidth: 0, overflow: 'hidden',
      }}
    >
      <div style={{
        width: 24, height: 24, background: C.bg,
        backgroundImage: player.avatar_url ? `url(${player.avatar_url})` : undefined,
        backgroundSize: 'cover', backgroundPosition: 'center',
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        flexShrink: 0,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontFamily: F.display, fontSize: 13, color: C.off,
          letterSpacing: '0.02em', lineHeight: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {player.username || 'Player'}
          {isMe && (
            <span style={{
              marginLeft: 5, fontFamily: F.body, fontSize: 8,
              letterSpacing: '0.2em', color: C.red,
            }}>YOU</span>
          )}
        </div>
        <div style={{
          fontFamily: F.body, fontSize: 8, color: C.offDim,
          letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 1,
        }}>
          {rankSummary(player)}
        </div>
      </div>
    </div>
  );
}

// Map any backend mode string to one of the toggle's canonical ids.
// Keeps the segmented toggle correct even when the backend uses
// 'competitive' / 'comp' / '2v2' / etc.
function normaliseMode(m) {
  if (!m) return '5v5';
  const s = String(m).toLowerCase();
  if (s === 'wingman' || s === '2v2') return 'wingman';
  // anything else (5v5, competitive, comp, ...) maps to 5v5
  return '5v5';
}

// Central stage — match config controls in the middle of the arena.
function CentralStage({
  lobby, isLeader, onModeChange, onRegionChange, onOpenMap, onOpenRules,
  onRandomise, onStart, starting, partyMembers,
}) {
  const mode = normaliseMode(lobby.mode);
  const region = REGIONS.find((r) => r.id === lobby.region) || REGIONS[0];
  const hasEnough = true; // server-side enforces; UI optimistic
  return (
    <div style={{
      background: C.card,
      borderTop:    `1px solid ${C.border}`,
      borderRight:  `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      borderLeft:   `1px solid ${C.border}`,
      padding: '14px 18px 16px',
      display: 'flex', flexDirection: 'column', gap: 10,
      position: 'relative',
    }}>
      {/* Lobby code strip */}
      <div style={{
        textAlign: 'center', paddingBottom: 8,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          fontFamily: F.body, fontSize: 9, letterSpacing: '0.4em',
          textTransform: 'uppercase', color: C.offDim, marginBottom: 2,
        }}>Lobby Code</div>
        <div style={{
          fontFamily: F.display, fontSize: 26, color: C.off,
          letterSpacing: '0.32em', lineHeight: 1,
        }}>
          {lobby.code}
        </div>
      </div>

      {/* Mode selector */}
      <SegmentedToggle
        label="Mode"
        options={[
          { id: '5v5', label: '5v5' },
          { id: 'wingman', label: 'Wingman' },
        ]}
        value={mode}
        onChange={onModeChange}
        disabled={!isLeader}
      />

      {/* Region selector */}
      <div>
        <FieldLabel>Region</FieldLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {REGIONS.map((r) => (
            <RegionPill key={r.id} region={r} selected={r.id === region.id}
              disabled={!isLeader} onClick={() => onRegionChange(r.id)} />
          ))}
        </div>
      </div>

      {/* Map tile */}
      <div>
        <FieldLabel>Map</FieldLabel>
        <MapTile mapId={lobby.selected_map} mode={mode} onClick={onOpenMap} disabled={!isLeader} />
      </div>

      {/* Rules + Randomise row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Btn variant="secondary" size="sm" onClick={onOpenRules}>
          ⚙ Match Rules
        </Btn>
        <Btn variant="secondary" size="sm" onClick={onRandomise} disabled={!isLeader}>
          ⇄ Randomise
        </Btn>
      </div>

      {/* START button */}
      <div style={{ marginTop: 2 }}>
        <StartButton
          disabled={!isLeader || starting || !hasEnough}
          loading={starting}
          onClick={onStart}
        />
        {!isLeader && (
          <div style={{
            marginTop: 8, textAlign: 'center',
            fontFamily: F.body, fontSize: 11, color: C.offDim, letterSpacing: '0.1em',
          }}>
            Waiting for the leader to start.
          </div>
        )}
      </div>
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 6,
    }}>
      <div style={{
        fontFamily: F.body, fontSize: 10, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: C.offDim,
      }}>{children}</div>
      {hint && (
        <div style={{
          fontFamily: F.body, fontSize: 10, color: C.offGhost,
        }}>{hint}</div>
      )}
    </div>
  );
}

function SegmentedToggle({ label, options, value, onChange, disabled }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        background: C.bg,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        padding: 3, gap: 3,
      }}>
        {options.map((o) => {
          const selected = o.id === value;
          return (
            <button
              key={o.id}
              disabled={disabled}
              onClick={() => onChange(o.id)}
              style={{
                padding: '8px 6px',
                background: selected ? C.red : 'transparent',
                color: selected ? C.off : C.offDim,
                border: 'none',
                fontFamily: F.display, fontSize: 15, letterSpacing: '0.1em',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled && !selected ? 0.5 : 1,
                transition: 'background 120ms ease, color 120ms ease',
              }}
            >{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function RegionPill({ region, selected, disabled, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '7px 6px',
        background: selected ? 'rgba(232,25,44,0.12)' : C.bg,
        color: selected ? C.off : C.offDim,
        borderTop:    `1px solid ${selected ? C.red : (hover && !disabled ? C.borderHi : C.border)}`,
        borderRight:  `1px solid ${selected ? C.red : (hover && !disabled ? C.borderHi : C.border)}`,
        borderBottom: `1px solid ${selected ? C.red : (hover && !disabled ? C.borderHi : C.border)}`,
        borderLeft:   `1px solid ${selected ? C.red : (hover && !disabled ? C.borderHi : C.border)}`,
        fontFamily: F.body, fontSize: 12, letterSpacing: '0.04em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 6,
        opacity: disabled && !selected ? 0.55 : 1,
        transition: 'border-color 120ms ease, background 120ms ease',
      }}
    >
      <span style={{ fontSize: 13 }}>{region.flag}</span>
      <span>{region.label}</span>
    </button>
  );
}

function StartButton({ onClick, disabled, loading }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%', position: 'relative',
        background: disabled ? C.cardHi : C.red,
        color: C.off,
        border: 'none',
        padding: '14px 0',
        fontFamily: F.display, fontSize: 26, letterSpacing: '0.15em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        boxShadow: hover && !disabled
          ? `0 0 0 4px rgba(232,25,44,0.18), 0 12px 32px rgba(232,25,44,0.45)`
          : `0 6px 20px rgba(232,25,44,0.3)`,
        transition: 'box-shadow 200ms ease, transform 120ms ease',
        transform: hover && !disabled ? 'translateY(-1px)' : 'translateY(0)',
        overflow: 'hidden',
      }}
    >
      <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 8, height: 8, background: C.off }} />
      <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 8, height: 8, background: C.off }} />
      {loading ? 'Spinning up server…' : 'Start Match'}
    </button>
  );
}

// Match rules sheet content.
function MatchRulesPanel({ lobby, isLeader, onKnife, onAllTalk, onTeamLock }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <RuleRow
        label="Knife Round"
        tip={RULE_TOOLTIPS.knife}
        value={!!lobby.knife_round}
        onChange={onKnife}
        disabled={!isLeader}
      />
      <RuleRow
        label="All-Talk"
        tip={RULE_TOOLTIPS.allTalk}
        value={!!lobby.all_talk}
        onChange={onAllTalk}
        disabled={!isLeader}
      />
      <RuleRow
        label="Team Lock"
        tip={RULE_TOOLTIPS.teamLock}
        value={!!lobby.teams_locked}
        onChange={onTeamLock}
        disabled={!isLeader}
      />
    </div>
  );
}

function RuleRow({ label, tip, value, onChange, disabled }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 20px',
      background: C.bg,
      borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${value ? C.red : C.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          fontFamily: F.display, fontSize: 22, color: C.off, letterSpacing: '0.04em',
        }}>{label}<span style={{ color: C.red }}>.</span></div>
        <InfoTip text={tip} />
      </div>
      <Toggle value={value} onChange={() => onChange(!value)} disabled={disabled} />
    </div>
  );
}

function Toggle({ value, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      style={{
        position: 'relative',
        width: 56, height: 28,
        background: value ? C.red : C.cardHi,
        borderTop: `1px solid ${value ? C.red : C.border}`,
        borderRight: `1px solid ${value ? C.red : C.border}`,
        borderBottom: `1px solid ${value ? C.red : C.border}`,
        borderLeft: `1px solid ${value ? C.red : C.border}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 160ms ease, border-color 160ms ease',
        padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: value ? 28 : 2,
        width: 22, height: 22, background: C.off,
        transition: 'left 180ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      }} />
    </button>
  );
}

// Slot context menu (kick/promote/remove bot).
function SlotMenu({ target, anchor, onClose, onKick, onPromote, onRemoveBot }) {
  const [pos, setPos] = useState({ top: -9999, left: -9999, opacity: 0 });
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const m = ref.current.getBoundingClientRect();
    let left = a.right - m.width;
    let top = a.bottom + 6;
    if (left < 8) left = 8;
    if (left + m.width > window.innerWidth - 8) left = window.innerWidth - m.width - 8;
    if (top + m.height > window.innerHeight - 8) top = a.top - m.height - 6;
    setPos({ top, left, opacity: 1 });
  }, [anchor]);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  const items = target.kind === 'bot'
    ? [{ label: 'Remove Bot', danger: true, fn: () => { onRemoveBot(target.bot); onClose(); } }]
    : [
        { label: 'Promote to Leader', fn: () => { onPromote(target.user); onClose(); } },
        { label: 'Kick from Lobby', danger: true, fn: () => { onKick(target.user); onClose(); } },
      ];

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed', top: pos.top, left: pos.left, opacity: pos.opacity,
        zIndex: 250, minWidth: 200,
        background: C.card,
        borderTop: `1px solid ${C.borderHi}`, borderRight: `1px solid ${C.borderHi}`,
        borderBottom: `1px solid ${C.borderHi}`, borderLeft: `1px solid ${C.borderHi}`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
        padding: 4, transition: 'opacity 120ms ease',
      }}
    >
      {items.map((it, i) => (
        <button
          key={i}
          onClick={it.fn}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: it.danger ? C.red : C.off,
            padding: '10px 14px',
            fontFamily: F.body, fontSize: 14, letterSpacing: '0.05em',
            transition: 'background 100ms ease',
          }}
          onMouseEnter={(e) => e.target.style.background = C.cardHi}
          onMouseLeave={(e) => e.target.style.background = 'transparent'}
        >{it.label}</button>
      ))}
    </div>
  );
}

// Top bar shown across phases 1–4.
function LobbyTopBar({ lobby, isLeader, onLeave, onInvite, onCopyCode, onDisband, allowDisband, onToggleVisibility }) {
  const [hoverVis, setHoverVis] = useState(false);
  const canToggleVis = isLeader && !!onToggleVisibility;
  return (
    <div style={{
      position: 'relative', zIndex: 5,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '20px 32px',
      borderBottom: `1px solid ${C.border}`,
      background: 'rgba(12,9,5,0.6)',
      backdropFilter: 'blur(8px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Diamond size={14} filled />
        <div>
          <div style={{
            fontFamily: F.body, fontSize: 10, letterSpacing: '0.35em',
            textTransform: 'uppercase', color: C.offDim,
          }}>Custom Match</div>
          <div style={{
            fontFamily: F.display, fontSize: 24, color: C.off,
            letterSpacing: '0.04em', lineHeight: 1,
          }}>
            {lobby.code}<span style={{ color: C.red }}>.</span>
          </div>
        </div>
        <button onClick={onCopyCode} title="Copy code" style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: C.offDim, fontFamily: F.body, fontSize: 12,
          letterSpacing: '0.15em', textTransform: 'uppercase',
        }}>⧉ Copy</button>
        <button
          type="button"
          onClick={canToggleVis ? () => onToggleVisibility(!lobby.is_public) : undefined}
          disabled={!canToggleVis}
          onMouseEnter={() => setHoverVis(true)}
          onMouseLeave={() => setHoverVis(false)}
          title={canToggleVis ? (lobby.is_public ? 'Make lobby private' : 'Make lobby public') : 'Only the leader can change visibility'}
          style={{
            padding: '4px 12px',
            background: hoverVis && canToggleVis
              ? (lobby.is_public ? 'rgba(255,107,43,0.12)' : 'rgba(240,242,245,0.05)')
              : 'transparent',
            borderTop: `1px solid ${lobby.is_public ? C.orange : C.border}`,
            borderRight: `1px solid ${lobby.is_public ? C.orange : C.border}`,
            borderBottom: `1px solid ${lobby.is_public ? C.orange : C.border}`,
            borderLeft: `1px solid ${lobby.is_public ? C.orange : C.border}`,
            color: lobby.is_public ? C.orange : C.offDim,
            fontFamily: F.body, fontSize: 11, letterSpacing: '0.2em',
            textTransform: 'uppercase',
            cursor: canToggleVis ? 'pointer' : 'default',
            transition: 'background 120ms ease',
          }}
        >
          {lobby.is_public ? '◉ Public' : '○ Private'}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Btn variant="secondary" size="sm" onClick={onInvite}>+ Invite</Btn>
        {isLeader && allowDisband && (
          <Btn variant="danger" size="sm" onClick={onDisband}>Disband</Btn>
        )}
        <Btn variant="ghost" size="sm" onClick={onLeave}>Leave Lobby</Btn>
      </div>
    </div>
  );
}

// Friend invite list inside a sheet.
function FriendInviteSheet({ friends, cooldowns, onInvite }) {
  if (!friends || friends.length === 0) {
    return (
      <div style={{
        padding: '32px 0', textAlign: 'center',
        fontFamily: F.body, fontSize: 14, color: C.offDim,
      }}>
        No friends yet. Add some from the dashboard.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {friends.map((f) => {
        const cdMs = cooldowns[f.username] ? cooldowns[f.username] - Date.now() : 0;
        const inCd = cdMs > 0;
        return (
          <div key={f.username} style={{
            padding: '12px 16px',
            background: C.bg,
            borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
            borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 34, height: 34, background: C.card,
                backgroundImage: f.avatar_url ? `url(${f.avatar_url})` : undefined,
                backgroundSize: 'cover', backgroundPosition: 'center',
                borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
                borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
              }} />
              <div>
                <div style={{ fontFamily: F.display, fontSize: 16, color: C.off }}>{f.username}</div>
                <div style={{ fontFamily: F.body, fontSize: 11, color: C.offDim, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {f.online ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
            <Btn
              variant={inCd ? 'ghost' : 'secondary'}
              size="sm"
              disabled={inCd}
              onClick={() => onInvite(f.username)}
            >
              {inCd ? `✓ Sent (${Math.ceil(cdMs / 1000)}s)` : 'Invite'}
            </Btn>
          </div>
        );
      })}
    </div>
  );
}

// Confirm dialog (kick, disband, etc).
function Confirm({ open, title, body, confirmLabel, danger, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <Sheet open={open} onClose={onClose} title={title} width={520}>
      <p style={{
        fontFamily: F.body, fontSize: 15, color: C.offDim,
        margin: 0, marginBottom: 24, lineHeight: 1.5,
      }}>{body}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" size="md" onClick={onClose}>Cancel</Btn>
        <Btn variant={danger ? 'primary' : 'secondary'} size="md" onClick={onConfirm}>
          {confirmLabel}
        </Btn>
      </div>
    </Sheet>
  );
}

// Console connect string — the fallback for when steam:// doesn't trigger.
// Shows the raw `connect ip; password pwd` command with a copy button.
function ConsoleConnectString({ ip, pwd }) {
  const [copied, setCopied] = useState(false);
  const [showCmd, setShowCmd] = useState(false);
  const cmd = pwd ? `connect ${ip}; password ${pwd}` : `connect ${ip}`;

  const copy = () => {
    navigator.clipboard?.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
      <button
        onClick={() => setShowCmd((s) => !s)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: C.offDim,
          padding: 0, marginBottom: showCmd ? 8 : 0,
        }}
      >
        {showCmd ? '▲ Hide console command' : '▼ CS:GO console command (manual)'}
      </button>
      {showCmd && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: C.bg,
          borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
          padding: '10px 14px',
        }}>
          <code style={{
            fontFamily: 'monospace', fontSize: 12, color: C.off,
            flex: 1, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            // Blur the password portion in the command unless explicitly shown
            filter: 'none',
          }}>
            {`connect ${ip};`}{pwd ? ` password ••••••••` : ''}
          </code>
          <button
            onClick={copy}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: copied ? C.red : C.offDim,
              fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
              textTransform: 'uppercase', padding: 4, flexShrink: 0,
            }}
          >{copied ? '✓ Copied' : '⧉ Copy (with password)'}</button>
        </div>
      )}
    </div>
  );
}

// Server connection panel — shows IP, password (masked by default),
// copy buttons, and a one-click "Connect" that fires the steam:// URL.
// Renders nothing if the server isn't reachable yet (no server_ip).
function ServerConnectionPanel({ lobby, compact = false, connectReady = true }) {
  const [showPwd, setShowPwd] = useState(false);
  const [ipCopied, setIpCopied] = useState(false);
  const [pwdCopied, setPwdCopied] = useState(false);
  const [countdown, setCountdown] = useState(30);

  const ip = lobby?.server_ip;
  const port = lobby?.server_port || 27015;
  const pwd = lobby?.server_password || '';

  if (!ip) return null;

  // Countdown ticker — only runs while !connectReady
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (connectReady) return;
    setCountdown(30);
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(t); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [connectReady, ip]);

  const addr = `${ip}:${port}`;
  const connectUrl = pwd
    ? `steam://connect/${addr}/${encodeURIComponent(pwd)}`
    : `steam://connect/${addr}`;

  const copyIp = () => {
    const full = pwd ? `connect ${addr}; password ${pwd}` : `connect ${addr}`;
    navigator.clipboard?.writeText(full);
    setIpCopied(true);
    setTimeout(() => setIpCopied(false), 1500);
  };
  const copyPwd = () => {
    const full = pwd ? `connect ${addr}; password ${pwd}` : `connect ${addr}`;
    navigator.clipboard?.writeText(full);
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 1500);
  };

  // Mask string with bullets while preserving length up to a cap.
  const masked = pwd ? '•'.repeat(Math.min(pwd.length, 12)) : '—';

  return (
    <div style={{
      background: C.card,
      borderTop: `1px solid ${C.borderHi}`,
      borderRight: `1px solid ${C.borderHi}`,
      borderBottom: `1px solid ${C.borderHi}`,
      borderLeft: `2px solid ${C.red}`,
      padding: compact ? '12px 16px' : '20px 24px',
      display: 'grid',
      gridTemplateColumns: compact ? '1fr 1fr auto' : '1fr 1fr',
      gap: compact ? 12 : 18,
      alignItems: 'center',
      maxWidth: compact ? 'none' : 640,
      margin: compact ? '0' : '0 auto',
      textAlign: 'left',
    }}>
      {/* IP field */}
      <div>
        <div style={{
          fontFamily: F.body, fontSize: 9, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: C.offDim, marginBottom: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Server IP</span>
          <span style={{ color: C.offGhost }}>Port {port}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: compact ? 13 : 16,
            color: C.off, letterSpacing: '0.05em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1, minWidth: 0,
          }}>{addr}</span>
          <button
            onClick={copyIp}
            title="Copy IP"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: ipCopied ? C.red : C.offDim,
              fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
              textTransform: 'uppercase', padding: 4,
              flexShrink: 0,
            }}
          >{ipCopied ? '✓ Copied' : '⧉ Copy connect'}</button>
        </div>
      </div>

      {/* Password field — masked by default */}
      <div>
        <div style={{
          fontFamily: F.body, fontSize: 9, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: C.offDim, marginBottom: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Password</span>
          <button
            onClick={() => setShowPwd((s) => !s)}
            title={showPwd ? 'Hide password' : 'Show password'}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: showPwd ? C.red : C.offDim,
              fontFamily: F.body, fontSize: 9, letterSpacing: '0.2em',
              textTransform: 'uppercase', padding: 0,
            }}
          >{showPwd ? '◉ Hide' : '○ Show'}</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: compact ? 13 : 16,
            color: showPwd ? C.off : C.offDim, letterSpacing: '0.1em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1, minWidth: 0,
            // Prevent screen-readers / OCR from picking up the masked value
            // when hidden (cosmetic only — clipboard still has the real value).
            userSelect: showPwd ? 'text' : 'none',
          }}>{showPwd ? pwd : masked}</span>
          <button
            onClick={copyPwd}
            title="Copy password"
            disabled={!pwd}
            style={{
              background: 'transparent', border: 'none',
              cursor: pwd ? 'pointer' : 'not-allowed',
              color: pwdCopied ? C.red : C.offDim,
              fontFamily: F.body, fontSize: 10, letterSpacing: '0.2em',
              textTransform: 'uppercase', padding: 4,
              flexShrink: 0,
              opacity: pwd ? 1 : 0.4,
            }}
          >{pwdCopied ? '✓ Copied' : '⧉ Copy connect'}</button>
        </div>
      </div>

      {/* Connect button or loading countdown */}
      {compact ? (
        connectReady ? (
          <a
            href={connectUrl}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '10px 20px',
              background: C.red, color: C.off,
              fontFamily: F.display, fontSize: 14, letterSpacing: '0.15em',
              textTransform: 'uppercase', textDecoration: 'none',
              whiteSpace: 'nowrap', flexShrink: 0,
              boxShadow: '0 4px 14px rgba(232,25,44,0.3)',
            }}
          >→ Connect</a>
        ) : (
          <div style={{
            padding: '10px 16px',
            borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
            borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
            fontFamily: F.display, fontSize: 13, color: C.offDim,
            letterSpacing: '0.1em', whiteSpace: 'nowrap', flexShrink: 0,
            textAlign: 'center',
          }}>
            Loading… {countdown}s
          </div>
        )
      ) : (
        <>
          {connectReady ? (
            <a
              href={connectUrl}
              style={{
                gridColumn: '1 / -1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '14px 0',
                background: C.red, color: C.off,
                fontFamily: F.display, fontSize: 20, letterSpacing: '0.18em',
                textTransform: 'uppercase', textDecoration: 'none',
                marginTop: 4,
                boxShadow: '0 6px 18px rgba(232,25,44,0.35)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 8, height: 8, background: C.off }} />
              <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 8, height: 8, background: C.off }} />
              Connect to Server
            </a>
          ) : (
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 14, padding: '14px 0', marginTop: 4,
              background: C.cardHi,
              borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
            }}>
              <div style={{
                width: 12, height: 12, transform: 'rotate(45deg)',
                borderTop: `2px solid ${C.red}`, borderRight: `2px solid ${C.red}`,
                borderBottom: `2px solid ${C.red}`, borderLeft: `2px solid ${C.red}`,
                animation: 'spinDiamond 1.4s linear infinite',
              }} />
              <span style={{
                fontFamily: F.display, fontSize: 18, color: C.offDim,
                letterSpacing: '0.12em',
              }}>
                CS:GO loading — connect in {countdown}s
              </span>
            </div>
          )}
          <ConsoleConnectString ip={addr} pwd={pwd} />
        </>
      )}
    </div>
  );
}

// ─── Phase 2: PROVISIONING ─────────────────────────────────────────────────
function Provisioning({ lobby, progress, message, connectReady }) {
  // progress: 0–100 from /server-progress polling
  return (
    <div style={{
      position: 'relative', zIndex: 2,
      minHeight: 'calc(100vh - 80px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 32,
    }}>
      <div style={{ textAlign: 'center', maxWidth: 720 }}>
        <div style={{
          fontFamily: F.body, fontSize: 12, letterSpacing: '0.4em',
          textTransform: 'uppercase', color: C.red, marginBottom: 16,
        }}>
          <span style={{ display: 'inline-block', animation: 'pulse 1.4s ease-in-out infinite' }}>● Provisioning</span>
        </div>

        <H size={108} style={{ marginBottom: 16 }}>Spinning up</H>
        <p style={{
          fontFamily: F.body, fontSize: 18, color: C.offDim,
          margin: 0, marginBottom: 48,
        }}>
          {message || 'Booting a dedicated server. This usually takes 30–90 seconds.'}
        </p>

        {/* Diamond-shaped progress */}
        <div style={{
          position: 'relative',
          width: 320, height: 320, margin: '0 auto 40px',
        }}>
          <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
            <defs>
              <linearGradient id="diamondFill" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={C.red} />
                <stop offset="100%" stopColor={C.orange} />
              </linearGradient>
              <clipPath id="diamondClip">
                <polygon points="100,10 190,100 100,190 10,100" />
              </clipPath>
            </defs>

            {/* outer diamond outline */}
            <polygon points="100,10 190,100 100,190 10,100"
              fill="none" stroke={C.border} strokeWidth="2" />

            {/* fill (clipped) */}
            <g clipPath="url(#diamondClip)">
              <rect x="0" y={200 - (progress * 2)} width="200" height={progress * 2}
                fill="url(#diamondFill)"
                style={{ transition: 'y 600ms cubic-bezier(0.4, 0, 0.2, 1), height 600ms cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            </g>

            {/* inner diamond outline overlay */}
            <polygon points="100,10 190,100 100,190 10,100"
              fill="none" stroke={C.red} strokeWidth="2"
              opacity="0.4" />

            {/* % text */}
            <text x="100" y="108" textAnchor="middle"
              fontFamily="'Bebas Neue', sans-serif" fontSize="48" fill={C.off}
              letterSpacing="2">
              {Math.round(progress)}%
            </text>
          </svg>
        </div>

        {/* Server connection details — only renders once server_ip is set. */}
        {/* Sits above the lobby info line so it's prominent.               */}
        <div style={{ marginBottom: 24 }}>
          <ServerConnectionPanel lobby={lobby} connectReady={connectReady} />
        </div>

        <div style={{
          fontFamily: F.body, fontSize: 13, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: C.offDim,
        }}>
          Lobby {lobby.code} · Server in {REGIONS.find((r) => r.id === lobby.region)?.label || lobby.region}
        </div>
      </div>
    </div>
  );
}

// ─── Phase 3: IN-MATCH (CS:GO tab scoreboard) ──────────────────────────────
function InMatch({ lobby, stats, myUserId, connectReady }) {
  // stats: array of player rows: { steam_id, username, team, kills, deaths, assists, headshots, hs_percent, is_bot }
  const team1Score = lobby.team1_score || 0;
  const team2Score = lobby.team2_score || 0;
  const team1Name = lobby.team1_name || 'Team Alpha';
  const team2Name = lobby.team2_name || 'Team Bravo';

  // Defensive: stats should always be an array, but guard in case.
  const statsArr = Array.isArray(stats) ? stats : [];
  const team1Rows = statsArr.filter((r) => r.team === 1 || r.team === 'team1');
  const team2Rows = statsArr.filter((r) => r.team === 2 || r.team === 'team2');

  // sort each team by kills desc
  team1Rows.sort((a, b) => (b.kills || 0) - (a.kills || 0));
  team2Rows.sort((a, b) => (b.kills || 0) - (a.kills || 0));

  const roundHistory = lobby.round_history || [];

  return (
    <div style={{
      position: 'relative', zIndex: 2,
      maxWidth: 1440, margin: '0 auto', padding: '40px 32px 80px',
    }}>
      {/* Banner: live score */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center', gap: 32, marginBottom: 40,
      }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontFamily: F.body, fontSize: 11, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: C.red, marginBottom: 6,
          }}>Team One</div>
          <div style={{
            fontFamily: F.display, fontSize: 56, color: C.off,
            letterSpacing: '0.04em', lineHeight: 0.9,
          }}>{team1Name}<span style={{ color: C.red }}>.</span></div>
        </div>

        <div style={{
          fontFamily: F.display, fontSize: 120, color: C.off,
          letterSpacing: '0.04em', lineHeight: 0.9,
          display: 'flex', alignItems: 'center', gap: 28,
        }}>
          <span style={{ color: C.red }}>{team1Score}</span>
          <span style={{ color: C.offDim, fontSize: 64 }}>:</span>
          <span style={{ color: C.orange }}>{team2Score}</span>
        </div>

        <div>
          <div style={{
            fontFamily: F.body, fontSize: 11, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: C.orange, marginBottom: 6,
          }}>Team Two</div>
          <div style={{
            fontFamily: F.display, fontSize: 56, color: C.off,
            letterSpacing: '0.04em', lineHeight: 0.9,
          }}>{team2Name}<span style={{ color: C.orange }}>.</span></div>
        </div>
      </div>

      {/* Compact rejoin panel — for anyone who disconnected and needs to     */}
      {/* get back in. Only renders if the server is reachable.               */}
      <div style={{ marginBottom: 24 }}>
        <ServerConnectionPanel lobby={lobby} compact connectReady={connectReady} />
      </div>

      {/* Two stat tables */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <StatTable rows={team1Rows} accent={C.red} title={team1Name} myUserId={myUserId} />
        <StatTable rows={team2Rows} accent={C.orange} title={team2Name} myUserId={myUserId} />
      </div>

      {/* Round history timeline — full match map. All possible round slots */}
      {/* render as outlined cells; completed rounds fill in with the winning */}
      {/* team's accent colour. Gives a clear sense of progress at a glance.  */}
      <RoundHistoryTimeline
        history={roundHistory}
        mode={lobby.mode}
        team1Name={team1Name}
        team2Name={team2Name}
      />
    </div>
  );
}

// RoundHistoryTimeline — sequential cell layout, greyed out until won.
// In MR12 (5v5 first to 13) the match has up to 30 rounds (24 reg + 6 OT).
// In wingman (MR8 first to 9) it's up to 16. We render enough cells to
// reasonably cover regulation, then overflow extends if needed.
function RoundHistoryTimeline({ history, mode, team1Name, team2Name }) {
  const isWingman = mode === 'wingman' || mode === '2v2';
  // Regulation round count: MR12 = 24, MR8 = 16. Halftime sits at half of this.
  const regCount = isWingman ? 16 : 24;
  const halfPoint = regCount / 2;
  // Render enough cells to comfortably cover the full match. If overtime
  // pushes past regulation, extend on demand.
  const totalCells = Math.max(regCount, history.length);

  // Normalise history into a stable "team1" | "team2" | null array.
  const normalised = [];
  for (let i = 0; i < totalCells; i++) {
    const r = history[i];
    if (r == null) { normalised.push(null); continue; }
    const raw = typeof r === 'string' ? r : (r.winner || r.round_winner || '');
    if (raw === 'team1' || raw === 1 || raw === '1') normalised.push('team1');
    else if (raw === 'team2' || raw === 2 || raw === '2') normalised.push('team2');
    else normalised.push(null);
  }

  // Compute running tally per team for the header callouts.
  let t1Wins = 0, t2Wins = 0;
  normalised.forEach((w) => {
    if (w === 'team1') t1Wins++;
    if (w === 'team2') t2Wins++;
  });

  return (
    <div style={{
      background: C.card,
      borderTop:    `1px solid ${C.border}`,
      borderRight:  `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      borderLeft:   `1px solid ${C.border}`,
      padding: '20px 24px',
    }}>
      {/* Header row: title + per-team tallies */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <div style={{
          fontFamily: F.body, fontSize: 11, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: C.offDim,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Diamond size={7} filled />
          Round Timeline
          <span style={{ color: C.offGhost, marginLeft: 4 }}>
            {history.length}/{regCount}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <TeamTally name={team1Name} wins={t1Wins} accent={C.red} />
          <TeamTally name={team2Name} wins={t2Wins} accent={C.orange} />
        </div>
      </div>

      {/* Timeline cells — flex grid with halftime gap */}
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 4,
        position: 'relative',
      }}>
        {normalised.map((winner, i) => {
          const roundNum = i + 1;
          const accent = winner === 'team1' ? C.red : winner === 'team2' ? C.orange : null;
          const isLatest = winner != null && i === history.length - 1;
          const isPlayed = winner != null;
          // Halftime gap rendered after the half-point cell
          const showHalfGap = roundNum === halfPoint;

          return (
            <React.Fragment key={i}>
              <RoundCell
                roundNum={roundNum}
                winner={winner}
                accent={accent}
                isLatest={isLatest}
                isPlayed={isPlayed}
              />
              {showHalfGap && (
                <div style={{
                  width: 14, position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 1, height: '70%',
                    background: C.borderHi,
                  }} />
                  <div style={{
                    position: 'absolute', top: -14, left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: F.body, fontSize: 8,
                    letterSpacing: '0.3em', color: C.offGhost,
                    whiteSpace: 'nowrap',
                  }}>HALF</div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Round-number labels underneath, every 4 rounds */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 4,
        marginTop: 6, position: 'relative',
      }}>
        {normalised.map((_, i) => {
          const roundNum = i + 1;
          const showLabel = roundNum === 1 || roundNum % 4 === 0 || roundNum === regCount;
          return (
            <React.Fragment key={i}>
              <div style={{
                flex: 1, minWidth: 22,
                fontFamily: F.body, fontSize: 9, letterSpacing: '0.1em',
                color: showLabel ? C.offDim : 'transparent',
                textAlign: 'center',
              }}>
                {showLabel ? roundNum : '·'}
              </div>
              {roundNum === halfPoint && <div style={{ width: 14 }} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function TeamTally({ name, wins, accent }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: F.body, fontSize: 11, letterSpacing: '0.15em',
      textTransform: 'uppercase',
    }}>
      <div style={{
        width: 8, height: 8, background: accent,
      }} />
      <span style={{ color: C.offDim, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
      <span style={{
        fontFamily: F.display, fontSize: 18, color: C.off,
        letterSpacing: '0.05em',
      }}>{wins}</span>
    </div>
  );
}

function RoundCell({ roundNum, winner, accent, isLatest, isPlayed }) {
  // Three visual states:
  //   • Unplayed → outlined empty cell
  //   • Won by a team → solid accent fill
  //   • Latest played → solid fill + subtle glow for "just happened" emphasis
  return (
    <div style={{
      flex: 1, minWidth: 22, height: 36,
      position: 'relative',
      background: isPlayed ? accent : 'transparent',
      borderTop:    `1px solid ${isPlayed ? accent : C.borderHi}`,
      borderRight:  `1px solid ${isPlayed ? accent : C.borderHi}`,
      borderBottom: `1px solid ${isPlayed ? accent : C.borderHi}`,
      borderLeft:   `1px solid ${isPlayed ? accent : C.borderHi}`,
      boxShadow: isLatest
        ? `0 0 0 2px ${accent === C.red ? 'rgba(232,25,44,0.25)' : 'rgba(255,107,43,0.25)'}, 0 0 12px ${accent === C.red ? 'rgba(232,25,44,0.4)' : 'rgba(255,107,43,0.4)'}`
        : 'none',
      transition: 'background 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
    }}>
      {/* Subtle inner stroke for unplayed cells to make them feel "ghosted" */}
      {!isPlayed && (
        <div style={{
          position: 'absolute', inset: 4,
          borderTop: `1px solid ${C.border}`, opacity: 0.5,
        }} />
      )}
      {/* Round number only shown for played cells, in white */}
      {isPlayed && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: F.display, fontSize: 11, color: C.off,
          letterSpacing: 0,
          textShadow: '0 1px 2px rgba(0,0,0,0.35)',
        }}>{roundNum}</div>
      )}
    </div>
  );
}

function StatTable({ rows, accent, title, myUserId }) {
  return (
    <div style={{
      background: C.card,
      borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
      borderBottom: `2px solid ${accent}`, borderLeft: `1px solid ${C.border}`,
    }}>
      <div style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Diamond size={8} filled color={accent} />
        <div style={{
          fontFamily: F.body, fontSize: 12, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: accent,
        }}>{title}</div>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.6fr 50px 50px 50px 50px 60px',
        padding: '12px 20px',
        fontFamily: F.body, fontSize: 10, letterSpacing: '0.25em',
        textTransform: 'uppercase', color: C.offDim,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div>Player</div>
        <div style={{ textAlign: 'right' }}>K</div>
        <div style={{ textAlign: 'right' }}>D</div>
        <div style={{ textAlign: 'right' }}>A</div>
        <div style={{ textAlign: 'right' }}>HS</div>
        <div style={{ textAlign: 'right' }}>HS%</div>
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <div style={{
          padding: '24px 20px', textAlign: 'center',
          fontFamily: F.body, fontSize: 13, color: C.offDim,
        }}>No data yet</div>
      ) : rows.map((r, i) => {
        const isMe = r.user_id && r.user_id === myUserId;
        return (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 50px 50px 50px 50px 60px',
            padding: '12px 20px', alignItems: 'center',
            borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : 'none',
            background: isMe ? 'rgba(232,25,44,0.06)' : 'transparent',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: F.display, fontSize: 17, color: C.off,
              letterSpacing: '0.02em', minWidth: 0,
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.username}</span>
              {r.is_bot && (
                <span style={{
                  fontFamily: F.body, fontSize: 9, letterSpacing: '0.2em',
                  textTransform: 'uppercase', color: C.offDim,
                  padding: '1px 5px',
                  borderTop: `1px solid ${C.offGhost}`, borderRight: `1px solid ${C.offGhost}`,
                  borderBottom: `1px solid ${C.offGhost}`, borderLeft: `1px solid ${C.offGhost}`,
                }}>Bot</span>
              )}
            </div>
            <div style={{ textAlign: 'right', fontFamily: F.display, fontSize: 18, color: C.off }}>{r.kills || 0}</div>
            <div style={{ textAlign: 'right', fontFamily: F.display, fontSize: 18, color: C.offDim }}>{r.deaths || 0}</div>
            <div style={{ textAlign: 'right', fontFamily: F.display, fontSize: 18, color: C.offDim }}>{r.assists || 0}</div>
            <div style={{ textAlign: 'right', fontFamily: F.display, fontSize: 18, color: C.offDim }}>{r.headshots || 0}</div>
            <div style={{ textAlign: 'right', fontFamily: F.display, fontSize: 18, color: accent }}>
              {(r.hs_percent != null ? Math.round(r.hs_percent) : 0)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Phase 4: MATCH-FINISHED ───────────────────────────────────────────────
function MatchOver({ lobby, stats, isLeader, onPlayAgain, onDisband, working }) {
  const team1Score = lobby.team1_score || 0;
  const team2Score = lobby.team2_score || 0;
  const team1Name = lobby.team1_name || 'Team Alpha';
  const team2Name = lobby.team2_name || 'Team Bravo';
  const winner = team1Score > team2Score ? 1 : (team2Score > team1Score ? 2 : 0);
  const winnerName = winner === 1 ? team1Name : (winner === 2 ? team2Name : null);
  const winnerAccent = winner === 1 ? C.red : (winner === 2 ? C.orange : C.offDim);

  // Defensive: stats should always be an array, but guard in case.
  const statsArr = Array.isArray(stats) ? stats : [];

  return (
    <div style={{
      position: 'relative', zIndex: 2,
      maxWidth: 1280, margin: '0 auto', padding: '64px 32px 96px',
    }}>
      {/* Verdict */}
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <div style={{
          fontFamily: F.body, fontSize: 13, letterSpacing: '0.4em',
          textTransform: 'uppercase', color: C.offDim, marginBottom: 12,
        }}>Match Over</div>
        {winner === 0 ? (
          <H size={120}>Draw</H>
        ) : (
          <>
            <H size={140} style={{ color: winnerAccent }}>{winnerName}</H>
            <div style={{
              fontFamily: F.display, fontSize: 36, color: C.off,
              letterSpacing: '0.1em', marginTop: 8,
            }}>Wins</div>
          </>
        )}

        <div style={{
          fontFamily: F.display, fontSize: 96, color: C.off,
          letterSpacing: '0.06em', marginTop: 32,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24,
        }}>
          <span style={{ color: C.red }}>{team1Score}</span>
          <span style={{ color: C.offDim, fontSize: 56 }}>—</span>
          <span style={{ color: C.orange }}>{team2Score}</span>
        </div>
      </div>

      {/* Stats summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 56 }}>
        <StatTable
          rows={statsArr.filter((r) => r.team === 1 || r.team === 'team1').sort((a, b) => (b.kills || 0) - (a.kills || 0))}
          accent={C.red}
          title={team1Name}
        />
        <StatTable
          rows={statsArr.filter((r) => r.team === 2 || r.team === 'team2').sort((a, b) => (b.kills || 0) - (a.kills || 0))}
          accent={C.orange}
          title={team2Name}
        />
      </div>

      {/* Action row */}
      {isLeader ? (
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16,
        }}>
          <Btn variant="primary" size="xl" onClick={onPlayAgain} disabled={working}>
            {working ? 'Resetting…' : 'Play Again →'}
          </Btn>
          <Btn variant="danger" size="xl" onClick={onDisband} disabled={working}>
            Disband Lobby
          </Btn>
        </div>
      ) : (
        <div style={{
          textAlign: 'center', fontFamily: F.body, fontSize: 14,
          letterSpacing: '0.2em', textTransform: 'uppercase', color: C.offDim,
        }}>
          Waiting for the leader to choose what's next.
        </div>
      )}
    </div>
  );
}

// ─── Lobby invite popup (incoming invitation, 20s countdown) ───────────────
function InvitePopup({ invite, onAccept, onDecline }) {
  const [remaining, setRemaining] = useState(20);
  useEffect(() => {
    if (!invite) return;
    setRemaining(20);
    const t = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) { onDecline(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [invite, onDecline]);

  if (!invite) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 32, right: 32, zIndex: 300,
      width: 360,
      background: C.card,
      borderTop: `1px solid ${C.borderHi}`, borderRight: `1px solid ${C.borderHi}`,
      borderBottom: `2px solid ${C.red}`, borderLeft: `1px solid ${C.borderHi}`,
      padding: 20,
      boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
      animation: 'sheetUp 220ms ease-out',
    }}>
      <div style={{
        fontFamily: F.body, fontSize: 11, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: C.red, marginBottom: 8,
      }}>Lobby Invite · {remaining}s</div>
      <div style={{
        fontFamily: F.display, fontSize: 22, color: C.off, marginBottom: 4,
      }}>{invite.from || 'Someone'}<span style={{ color: C.red }}>.</span></div>
      <div style={{
        fontFamily: F.body, fontSize: 13, color: C.offDim, marginBottom: 16,
      }}>Invited you to lobby <span style={{ color: C.off, letterSpacing: '0.1em' }}>{invite.code}</span></div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="primary" size="sm" onClick={onAccept} style={{ flex: 1 }}>Join</Btn>
        <Btn variant="ghost" size="sm" onClick={onDecline} style={{ flex: 1 }}>Decline</Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function CustomLobby() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // ─── Top-level state ────────────────────────────────────────────────────
  const [phase, setPhase] = useState('entry'); // entry | setup | provisioning | inmatch | matchover
  const [lobby, setLobby] = useState(null);
  const [publicLobbies, setPublicLobbies] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [friends, setFriends] = useState([]);
  const [stats, setStats] = useState([]);
  const [serverProgress, setServerProgress] = useState(0);
  const [serverMessage, setServerMessage] = useState('');
  const [starting, setStarting] = useState(false);
  const [working, setWorking] = useState(false);

  // sheets
  const [mapOpen, setMapOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // confirm dialogs
  const [confirmKick, setConfirmKick] = useState(null);
  const [confirmDisband, setConfirmDisband] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // slot menu
  const [slotMenu, setSlotMenu] = useState(null);
  const slotAnchorRef = useRef(null);

  // friend invite cooldowns
  const [inviteCooldowns, setInviteCooldowns] = useState({});
  const [toast, setToast] = useState(null);

  // Incoming invite popup — populated externally (e.g. by the notification
  // poll in Layout). The setter is held but typically driven from above; if
  // your Layout already renders invite popups globally, this stays dormant.
  // eslint-disable-next-line no-unused-vars
  const [incomingInvite, setIncomingInvite] = useState(null);

  // ─── Helpers ────────────────────────────────────────────────────────────
  const isLeader = useMemo(() => {
    return !!lobby && !!user && lobby.leader_id === user.id;
  }, [lobby, user]);

  const maxSizePerTeam = useMemo(() => {
    return normaliseMode(lobby?.mode) === 'wingman' ? 2 : 5;
  }, [lobby]);

  const team1Players = useMemo(() => (lobby?.players || []).filter((p) => p.team === 'team1' || p.team === 1), [lobby]);
  const team2Players = useMemo(() => (lobby?.players || []).filter((p) => p.team === 'team2' || p.team === 2), [lobby]);
  const team1Bots = useMemo(() => (lobby?.bots || []).filter((b) => b.team === 'team1' || b.team === 1), [lobby]);
  const team2Bots = useMemo(() => (lobby?.bots || []).filter((b) => b.team === 'team2' || b.team === 2), [lobby]);

  // Unassigned: any player not on team1 or team2. Covers null, undefined,
  // 'unassigned', '', or anything else the backend might use.
  const unassignedPlayers = useMemo(() => {
    return (lobby?.players || []).filter((p) => {
      const t = p.team;
      return t !== 'team1' && t !== 'team2' && t !== 1 && t !== 2;
    });
  }, [lobby]);

  // Which team am I on right now? Returns 1, 2, or null.
  const myCurrentTeam = useMemo(() => {
    if (!user) return null;
    if (team1Players.some((p) => (p.user_id || p.id) === user.id)) return 1;
    if (team2Players.some((p) => (p.user_id || p.id) === user.id)) return 2;
    return null;
  }, [team1Players, team2Players, user]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const isPageVisible = useCallback(() =>
    typeof document === 'undefined' || document.visibilityState !== 'hidden',
  []);

  // ─── Public lobby browser ───────────────────────────────────────────────
  const refreshPublic = useCallback(async () => {
    setLoadingPublic(true);
    try {
      const data = unwrap(await getPublicLobbies());
      // Could be an array directly, or { lobbies: [...] }.
      const list =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.lobbies) && data.lobbies) ||
        [];
      setPublicLobbies(list);
    } catch (e) {
      console.error('public lobbies', e);
      setPublicLobbies([]);
    }
    setLoadingPublic(false);
  }, []);

  useEffect(() => {
    if (phase !== 'entry') return;
    refreshPublic();

    const refreshIfVisible = () => {
      if (isPageVisible()) refreshPublic();
    };

    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [phase, refreshPublic, isPageVisible]);

  // ─── Load friends (for invite sheet) ────────────────────────────────────
  useEffect(() => {
    if (!user || !inviteOpen) return;
    (async () => {
      try {
        const data = unwrap(await getFriends());
        const raw =
          (Array.isArray(data) && data) ||
          (Array.isArray(data?.friends) && data.friends) ||
          [];
        const list = raw.map((f) => ({
          username: f.users?.username || f.username,
          avatar_url: f.users?.avatar_url || f.avatar_url,
          online: f.online ?? false,
        })).filter((f) => f.username);
        setFriends(list);
      } catch (e) { /* ignore */ }
    })();
  }, [user, inviteOpen]);

  // ─── Deep-link join via ?code=XXXX ──────────────────────────────────────
  useEffect(() => {
    const code = params.get('code');
    if (code && phase === 'entry' && !lobby) {
      doJoinByCode(code.toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ─── Restore current lobby on mount ─────────────────────────────────────
  // First try the cached lobby ID for fast same-browser resumes. If this is a
  // fresh browser/session, ask the backend whether this user is already in an
  // active lobby so they do not get stranded on the entry screen.
  useEffect(() => {
    const cachedId = localStorage.getItem('current_lobby_id');
    if (lobby || !user) return;

    let cancelled = false;
    (async () => {
      const restoreLobby = (lb) => {
        if (cancelled || !lb?.id) return false;
        const inLobby = (lb.players || []).some(
          (p) => (p.user_id || p.id) === user.id
        );
        if (!inLobby) return false;
        setLobby(lb);
        setPhase(derivePhaseFromLobby(lb));
        localStorage.setItem('current_lobby_id', lb.id);
        return true;
      };

      if (cachedId) {
        try {
          const data = unwrap(await getCustomLobby(cachedId));
          const lb = data?.lobby || data;
          if (restoreLobby(lb)) return;
          localStorage.removeItem('current_lobby_id');
        } catch (e) {
          // 404 or 403 — cached lobby is gone or we're not in it anymore.
          localStorage.removeItem('current_lobby_id');
        }
      }

      try {
        const data = unwrap(await getCurrentCustomLobby());
        const lb = data?.lobby || data;
        if (!restoreLobby(lb)) localStorage.removeItem('current_lobby_id');
      } catch (e) {
        localStorage.removeItem('current_lobby_id');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ─── Sync lobby.id → localStorage so other tabs/navigations can resume ──
  useEffect(() => {
    if (lobby?.id) localStorage.setItem('current_lobby_id', lobby.id);
  }, [lobby?.id]);

  // ─── Lobby creation ─────────────────────────────────────────────────────
  const doCreate = async () => {
    try {
      const data = unwrap(await createCustomLobby());
      const lb = data?.lobby || data;
      if (!lb || !lb.id) throw new Error('Could not create lobby');
      setLobby(lb);
      setPhase('setup');
    } catch (e) {
      // Backend might tell us we're already in a lobby — try to recover by
      // looking it up. The detail message + an embedded ID is one common
      // shape; if the backend returns the existing lobby in the error body
      // we use that, otherwise we suggest leaving.
      const detail = extractErr(e);
      const responseData = e?.response?.data;
      const existing =
        responseData?.lobby ||
        responseData?.existing_lobby ||
        responseData?.detail?.lobby ||
        responseData?.detail?.existing_lobby;
      if (existing?.id) {
        setLobby(existing);
        setPhase('setup');
        showToast('Resumed your existing lobby');
        return;
      }
      throw new Error(detail);
    }
  };

  const doJoinByCode = async (code) => {
    const data = unwrap(await joinCustomLobby(code));
    const lb = data?.lobby || data;
    if (!lb || !lb.id) throw new Error('Lobby not found');
    setLobby(lb);
    setPhase('setup');
  };

  // ─── Smart fallback refresh: lobby state ────────────────────────────────
  useEffect(() => {
    if (!lobby?.id || phase === 'entry') return;

    let cancelled = false;
    const tick = async () => {
      if (!isPageVisible()) return;
      try {
        const data = unwrap(await getCustomLobby(lobby.id));
        const lb = data?.lobby || data;
        if (cancelled || !lb) return;
        setLobby((prev) => ({ ...prev, ...lb }));

        // Phase transitions — derive from multiple signals, not just status.
        // This is what gives non-leaders the right view: they only see the
        // lobby object updating, so any signal of "server is being prepared"
        // (droplet_id, started_at, etc.) flips them to provisioning too.
        const nextPhase = derivePhaseFromLobby(lb);

        // Never auto-flip BACK to setup from matchover unless scores reset
        // (handled in handlePlayAgain). Otherwise apply whatever derivation said.
        if (phase === 'matchover' && nextPhase === 'setup'
            && (lb.team1_score > 0 || lb.team2_score > 0)) {
          // Stay on matchover — don't drop the user back to setup with
          // stale scores still showing.
        } else if (nextPhase !== phase) {
          setPhase(nextPhase);
        }
      } catch (e) { /* ignore */ }
    };
    const intervalMs =
      phase === 'provisioning' ? 5000 :
      phase === 'setup' ? 10000 :
      15000;

    tick();
    const t = setInterval(tick, intervalMs);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [lobby?.id, phase, isPageVisible]);

  // ─── Polling: server progress (during provisioning) ─────────────────────
  // The backend may or may not expose /server-progress. Either way, we use a
  // time-based asymptotic estimate so the diamond always *visibly* advances.
  // Real-server signal (if available) overrides the estimate when higher.
  // We complete to 100% when the lobby transitions out of provisioning.
  useEffect(() => {
    if (phase !== 'provisioning' || !lobby?.id) return;
    let cancelled = false;
    const startedAt = Date.now();
    let lastBackendProgress = 0;

    const computeEstimate = () => {
      // Asymptotic ease toward 95% over ~90 seconds. Never hits 95 exactly.
      const elapsed = (Date.now() - startedAt) / 1000; // seconds
      // 1 - e^(-elapsed / 30) approaches 1 as elapsed grows;
      // multiply by 95 so the cap is 95%, leaving the final 5% for the
      // "server is reachable" moment.
      return 95 * (1 - Math.exp(-elapsed / 30));
    };

    const updateEstimate = () => {
      if (cancelled) return;
      // Keep the diamond smooth locally without sending a request each tick.
      setServerProgress(Math.max(lastBackendProgress, computeEstimate()));
    };

    const pollBackend = async () => {
      if (!isPageVisible()) return;
      try {
        const data = await apiGet(`/custom-lobby/${lobby.id}/server-progress`);
        if (!cancelled && data && typeof data.progress === 'number') {
          lastBackendProgress = Math.max(lastBackendProgress, data.progress);
          if (data.message) setServerMessage(data.message);
          updateEstimate();
        }
      } catch {
        // Endpoint missing or unreachable — fall back to time-based only.
      }
    };

    // Smooth local progress, with backend checks slowed down substantially.
    updateEstimate();
    pollBackend();
    const uiTimer = setInterval(updateEstimate, 800);
    const backendTimer = setInterval(pollBackend, 5000);
    return () => {
      cancelled = true;
      clearInterval(uiTimer);
      clearInterval(backendTimer);
    };
  }, [phase, lobby?.id, isPageVisible]);

  // When provisioning completes (phase flips to inmatch), snap progress to 100.
  useEffect(() => {
    if (phase === 'inmatch') setServerProgress(100);
  }, [phase]);

  // ─── Polling: stats (in-match & match-over) ─────────────────────────────
  useEffect(() => {
    if (phase !== 'inmatch' && phase !== 'matchover') return;
    if (!lobby?.id) return;

    let cancelled = false;
    const tick = async () => {
      if (!isPageVisible()) return;
      try {
        const res = await getMatchStats(lobby.id);
        // getMatchStats returns raw axios response (no .data unwrap in api.js)
        // The actual payload is res.data. It could be:
        //   { players: [...] }  — most likely from custom_match_stats query
        //   [...]               — bare array
        //   { data: { players: [...] } } — double wrapped (unlikely but possible)
        const payload = res?.data ?? res;

        const rows =
          (Array.isArray(payload?.players) && payload.players) ||
          (Array.isArray(payload?.data?.players) && payload.data.players) ||
          (Array.isArray(payload) && payload) ||
          [];

        if (!cancelled) setStats(rows);
      } catch (e) {
        console.error('[Stats] fetch error:', e?.response?.data || e?.message);
      }
    };
    tick();
    const t = setInterval(tick, phase === 'inmatch' ? 15000 : 30000);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [phase, lobby?.id, isPageVisible]);

  // ─── Cooldown ticker ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setInviteCooldowns((c) => ({ ...c })), 1000);
    return () => clearInterval(t);
  }, []);

  // Derive the right phase from a lobby object. Uses multiple signals
  // because the backend doesn't always keep `status` synced perfectly with
  // what's actually happening server-side.
  //   - status 'in_match' / 'live' / 'playing' → in-match
  //   - status 'finished' / 'completed' → match over
  //   - status 'provisioning' / 'starting'   → provisioning
  //   - has droplet_id but no server_ip yet  → provisioning (still booting)
  //   - has server_ip and started_at         → in-match (server is live)
  //   - otherwise                            → setup
  const derivePhaseFromLobby = (lb) => {
    if (!lb) return 'setup';
    const status = (lb.status || '').toLowerCase();

    if (status === 'finished' || status === 'completed' || status === 'over') {
      return 'matchover';
    }
    // Cover every plausible "match is live" string the backend might use.
    if (['in_match', 'live', 'playing', 'active', 'started', 'in_progress', 'running'].includes(status)) {
      return 'inmatch';
    }
    if (['provisioning', 'starting', 'launching', 'booting'].includes(status)) {
      return 'provisioning';
    }

    // Status didn't tell us — fall back to server-side artefacts.
    if (lb.server_ip && lb.started_at) {
      return 'inmatch';
    }
    if (lb.droplet_id || lb.server_id || (lb.started_at && !lb.server_ip)) {
      return 'provisioning';
    }
    return 'setup';
  };
  // Extract the best human-readable error string from various shapes.
  // FastAPI uses response.data.detail (string or list of validation errors);
  // axios sets a generic .message; some endpoints return plain strings.
  const extractErr = (e) => {
    const data = e?.response?.data;
    // FastAPI validation errors: detail is an array of {loc, msg, type}
    if (Array.isArray(data?.detail)) {
      return data.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
    }
    if (typeof data?.detail?.message === 'string') return data.detail.message;
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      try { return JSON.stringify(data); } catch { /* ignore */ }
    }
    return e?.message || 'Something went wrong';
  };

  const safeCall = async (fn, label) => {
    try { await fn(); }
    catch (e) {
      console.error(label, e?.response?.data || e);
      showToast(`${label}: ${extractErr(e)}`);
    }
  };

  const refreshLobby = useCallback(async () => {
    if (!lobby?.id) return;
    try {
      const data = unwrap(await getCustomLobby(lobby.id));
      const lb = data?.lobby || data;
      if (lb) setLobby((prev) => ({ ...prev, ...lb }));
    } catch {/* ignore */}
  }, [lobby?.id]);

  const loadLobbySnapshot = useCallback(async () => {
    if (!lobby?.id) return null;
    const data = unwrap(await getCustomLobby(lobby.id));
    const lb = data?.lobby || data;
    if (lb) setLobby((prev) => ({ ...prev, ...lb }));
    return lb || null;
  }, [lobby?.id]);

  // The custom-lobby backend's accepted mode values aren't 100% confirmed.
  // Known parties.mode values are '5v5' / 'wingman' but the custom-lobby
  // mode column may differ. Try '5v5' first; if the backend rejects it,
  // fall back to 'competitive'. Remembers the working value across the session.
  const handleModeChange = (mode) =>
    safeCall(async () => {
      const tried = [];
      // For 5v5 try both common conventions; wingman is consistent everywhere.
      const candidates = mode === '5v5'
        ? ['5v5', 'competitive', 'comp']
        : ['wingman', '2v2'];

      let lastErr = null;
      for (const value of candidates) {
        try {
          console.log('[CustomLobby] setLobbyMode trying:', value);
          await setLobbyMode(lobby.id, value);
          console.log('[CustomLobby] setLobbyMode succeeded with:', value);
          tried.push({ value, ok: true });
          lastErr = null;
          break;
        } catch (e) {
          tried.push({ value, err: extractErr(e) });
          lastErr = e;
        }
      }
      if (lastErr) {
        console.error('[CustomLobby] all mode candidates failed:', tried);
        throw lastErr;
      }
      await refreshLobby();
    }, 'Set mode');

  const handleRegionChange = (region) =>
    safeCall(async () => { await setLobbyRegion(lobby.id, region); await refreshLobby(); }, 'Set region');

  const handleMapPick = (mapId) =>
    safeCall(async () => { await setLobbyMap(lobby.id, mapId); setMapOpen(false); await refreshLobby(); }, 'Set map');

  const handleRandomise = () =>
    safeCall(async () => { await randomiseTeams(lobby.id); await refreshLobby(); }, 'Randomise');

  const handleStart = async () => {
    setStarting(true);
    setServerProgress(0);
    try {
      await startCustomMatch(lobby.id);
      await refreshLobby();
      setPhase('provisioning');
    } catch (e) {
      // Log the full response so we can diagnose 400s from the backend.
      console.error('[CustomLobby] start failed — full error:', e);
      console.error('[CustomLobby] response data:', e?.response?.data);
      console.error('[CustomLobby] response status:', e?.response?.status);

      // Try every plausible source for a meaningful message before falling
      // back to the generic axios string.
      const detail = e?.response?.data?.detail
        ?? e?.response?.data?.message
        ?? (typeof e?.response?.data === 'string' ? e.response.data : null)
        ?? (e?.response?.data ? JSON.stringify(e.response.data) : null)
        ?? e?.message
        ?? 'Failed to start';
      showToast(`Start match: ${detail}`);
    }
    setStarting(false);
  };

  const handleRename = (team, name) =>
    safeCall(async () => { await setTeamName(lobby.id, `team${team}`, name); await refreshLobby(); }, 'Rename team');

  const handleNamesLockToggle = () =>
    safeCall(async () => {
      await setTeamNamesLock(lobby.id, !lobby.team_names_locked);
      await refreshLobby();
    }, 'Lock toggle');

  const handleKnife = (val) =>
    safeCall(async () => { await setKnifeRound(lobby.id, val); await refreshLobby(); }, 'Knife');

  const handleAllTalk = (val) =>
    safeCall(async () => { await setAllTalk(lobby.id, val); await refreshLobby(); }, 'All-talk');

  const handleTeamLock = (val) =>
    safeCall(async () => { await setTeamLock(lobby.id, val); await refreshLobby(); }, 'Team lock');

  const handleVisibility = (val) =>
    safeCall(async () => { await setLobbyVisibility(lobby.id, val); await refreshLobby(); }, 'Visibility');

  const handleAddBot = (team) =>
    safeCall(async () => {
      const teamKey = `team${team}`;
      const beforeCount = (lobby?.bots || []).filter((b) => b.team === teamKey || b.team === team).length;
      try {
        const data = unwrap(await addBots(lobby.id, teamKey, 1));
        const lb = data?.lobby || data;
        if (lb?.id) setLobby((prev) => ({ ...prev, ...lb }));
      } catch (e) {
        if (e?.message !== 'Network Error') throw e;
        const recovered = await loadLobbySnapshot();
        const afterCount = (recovered?.bots || []).filter((b) => b.team === teamKey || b.team === team).length;
        if (afterCount <= beforeCount) throw e;
      }
    }, 'Add bot');

  // Drag-and-drop state. Only meaningful when the user is the lobby leader.
  // dragging holds the player object currently being dragged, or null.
  const [dragging, setDragging] = useState(null);
  const [dragOverTeam, setDragOverTeam] = useState(null);

  // Connect delay — hold off showing the connect button for 30s after the
  // server IP appears. The server is live on the OS level but CS:GO and the
  // map are still loading. Prevents "failed to connect" from eager players.
  const [connectReady, setConnectReady] = useState(false);
  const connectTimerRef = useRef(null);

  useEffect(() => {
    const ip = lobby?.server_ip;
    if (ip) {
      // IP just appeared (or persists) — arm the timer once.
      // Clearing first ensures we don't double-set if the effect re-runs.
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = setTimeout(() => setConnectReady(true), 30000);
    } else {
      clearTimeout(connectTimerRef.current);
      setConnectReady(false);
    }
    return () => clearTimeout(connectTimerRef.current);
    // Intentionally only re-run when the IP itself changes, not connectReady.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobby?.server_ip]); // 1 | 2 | 'unassigned' | null

  const handleJoinTeam = (team) =>
    safeCall(async () => {
      const teamKey = `team${team}`;
      try {
        // Send the user's own ID with the team they want to join.
        const data = unwrap(await assignTeam(lobby.id, user.id, teamKey));
        const lb = data?.lobby || data;
        if (lb?.id) setLobby((prev) => ({ ...prev, ...lb }));
      } catch (e) {
        if (e?.message !== 'Network Error') throw e;
        const recovered = await loadLobbySnapshot();
        const moved = (recovered?.players || []).some(
          (p) => (p.user_id || p.id) === user.id && p.team === teamKey
        );
        if (!moved) throw e;
      }
    }, 'Join team');

  // Leader-only: assign any user to a team. Used by drag-and-drop.
  const handleAssignUser = (userId, team) =>
    safeCall(async () => {
      // team is 1, 2, or 'unassigned' (we send 'team1', 'team2', or 'none' — backend's choice)
      const teamStr = team === 1 ? 'team1' : team === 2 ? 'team2' : 'unassigned';
      try {
        const data = unwrap(await assignTeam(lobby.id, userId, teamStr));
        const lb = data?.lobby || data;
        if (lb?.id) setLobby((prev) => ({ ...prev, ...lb }));
      } catch (e) {
        if (e?.message !== 'Network Error') throw e;
        const recovered = await loadLobbySnapshot();
        const moved = (recovered?.players || []).some(
          (p) => (p.user_id || p.id) === userId && p.team === teamStr
        );
        if (!moved) throw e;
      }
    }, 'Assign player');

  const handleRemoveBot = (bot) =>
    safeCall(async () => {
      // Accept any of several plausible identifier fields. The custom_lobby_bots
      // schema is { id, lobby_id, bot_name, team } but joins or serialization
      // may rename id → bot_id, etc. Log so we can see what we actually got.
      console.log('[CustomLobby] removeBot bot object:', bot);
      const botIdentifier =
        bot?.id ?? bot?.bot_id ?? bot?.uuid ?? bot?.bot_name ?? bot?.name;
      if (!botIdentifier) {
        console.error('[CustomLobby] no identifier on bot:', bot);
        throw new Error('Bot has no identifier (check console)');
      }
      await removeBot(lobby.id, encodeURIComponent(String(botIdentifier)));
      await refreshLobby();
    }, 'Remove bot');

  const handlePromote = (u) =>
    safeCall(async () => { await promoteToCaptain(lobby.id, u.user_id || u.id); await refreshLobby(); }, 'Promote');

  const handleKickConfirmed = async (u) => {
    setConfirmKick(null);
    await safeCall(async () => {
      await kickFromLobby(lobby.id, u.user_id || u.id);
      await refreshLobby();
    }, 'Kick');
  };

  const handleSlotMenu = ({ kind, user: u, bot, team }, e) => {
    slotAnchorRef.current = e?.currentTarget || e?.target || null;
    setSlotMenu({ kind, user: u, bot, team, anchor: slotAnchorRef.current });
  };

  const handleInviteFriend = async (username) => {
    await safeCall(async () => {
      await inviteFriendToLobby(lobby.id, username);
      setInviteCooldowns((c) => ({ ...c, [username]: Date.now() + 30000 }));
      showToast(`Invite sent to ${username}`);
    }, 'Invite friend');
  };

  const handleLeaveConfirmed = async () => {
    setConfirmLeave(false);
    await safeCall(async () => { await leaveCustomLobby(lobby.id); }, 'Leave');
    localStorage.removeItem('current_lobby_id');
    setLobby(null);
    setPhase('entry');
    navigate('/custom');
  };

  const handleDisbandConfirmed = async () => {
    setConfirmDisband(false);
    await safeCall(async () => { await disbandLobby(lobby.id); }, 'Disband');
    localStorage.removeItem('current_lobby_id');
    setLobby(null);
    setPhase('entry');
    navigate('/custom');
  };

  const handlePlayAgain = async () => {
    setWorking(true);
    try {
      await resetLobby(lobby.id);
      await refreshLobby();
      // Clear match state, go back to team selection.
      // The backend should have cleared server_ip / droplet_id so the next
      // Start spins a fresh server with the new config.
      setStats([]);
      setServerProgress(0);
      setServerMessage('');
      setPhase('setup');
    } catch (e) {
      showToast(`Reset: ${extractErr(e)}`);
    }
    setWorking(false);
  };

  const handleCopyCode = () => {
    if (!lobby?.code) return;
    navigator.clipboard?.writeText(lobby.code);
    showToast('Code copied');
  };

  // ─── Drag-and-drop handlers ─────────────────────────────────────────────
  const handlePlayerDragStart = (player) => setDragging(player);
  const handlePlayerDragEnd = () => { setDragging(null); setDragOverTeam(null); };
  const handleDragOver = (target) => setDragOverTeam(target);
  const handleDragLeave = (target) => {
    // Clear only if we're leaving the target we'd been hovering
    setDragOverTeam((curr) => (curr === target ? null : curr));
  };
  const handleDropPlayer = async (userId, target) => {
    setDragging(null);
    setDragOverTeam(null);
    if (!userId || !isLeader) return;
    // No-op if dropping on the same team
    const current = (lobby?.players || []).find(
      (p) => (p.user_id || p.id) === userId
    );
    const currentTeam = current?.team;
    const targetStr = target === 1 ? 'team1' : target === 2 ? 'team2' : 'unassigned';
    if (currentTeam === targetStr) return;
    await handleAssignUser(userId, target);
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      color: C.off,
      fontFamily: F.body,
      position: 'relative',
      overflowX: 'hidden',
      animation: 'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both',
    }}>
      {/* Inline keyframes */}
      <style>{`
        @keyframes rp-page-up {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes sheetUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes sheetFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spinDiamond {
          from { transform: rotate(45deg); }
          to { transform: rotate(405deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 1ms !important; transition-duration: 1ms !important; }
        }
      `}</style>

      <DriftField />

      {/* Phase 0: Entry hub */}
      {phase === 'entry' && (
        <EntryHub
          onCreate={doCreate}
          onJoinCode={doJoinByCode}
          publicLobbies={publicLobbies}
          onJoinPublic={doJoinByCode}
          loadingPublic={loadingPublic}
          refreshPublic={refreshPublic}
        />
      )}

      {/* Phases 1–4: lobby exists */}
      {phase !== 'entry' && lobby && (
        <>
          <LobbyTopBar
            lobby={lobby}
            isLeader={isLeader}
            onLeave={() => setConfirmLeave(true)}
            onInvite={() => setInviteOpen(true)}
            onCopyCode={handleCopyCode}
            onDisband={() => setConfirmDisband(true)}
            allowDisband={phase === 'setup' || phase === 'matchover'}
            onToggleVisibility={handleVisibility}
          />

          {/* Phase 1: Setup arena */}
          {phase === 'setup' && (
            <div style={{
              position: 'relative', zIndex: 2,
              maxWidth: 1440, margin: '0 auto', padding: '20px 32px 40px',
            }}>
              {/* Heading row — title on the left, persistent bench on the right.
                  The bench is ALWAYS rendered now so it acts as a stable
                  strategic surface for moving players around pre-match. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, auto) minmax(620px, 1fr)',
                gap: 28,
                alignItems: 'end',
                marginBottom: 16,
              }}>
                {/* Title block */}
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4,
                    fontFamily: F.body, fontSize: 10, letterSpacing: '0.3em',
                    textTransform: 'uppercase', color: C.offDim,
                  }}>
                    <Diamond size={8} filled />
                    {isLeader ? 'You are the leader' : 'Waiting in lobby'}
                  </div>
                  <H size={48}>The arena</H>
                </div>

                {/* Persistent bench */}
                <UnassignedZone
                  players={unassignedPlayers}
                  isLeader={isLeader}
                  myUserId={user?.id}
                  dragging={dragging}
                  isDropTarget={dragOverTeam === 'unassigned'}
                  onDropPlayer={handleDropPlayer}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onPlayerDragStart={handlePlayerDragStart}
                  onPlayerDragEnd={handlePlayerDragEnd}
                  onJoinTeam={handleJoinTeam}
                />
              </div>

              {/* Arena grid: team1 | stage | team2 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 420px 1fr',
                gap: 24,
                alignItems: 'start',
              }}>
                <TeamSlab
                  team={1} side="left"
                  lobby={lobby}
                  players={team1Players}
                  bots={team1Bots}
                  maxSize={maxSizePerTeam}
                  canManage={isLeader}
                  isLeader={isLeader}
                  lobbyLeaderId={lobby.leader_id}
                  onRenameTeam={handleRename}
                  onTeamNamesLockToggle={handleNamesLockToggle}
                  onSlotMenu={(t, e) => handleSlotMenu(t, e)}
                  onAddBot={handleAddBot}
                  onJoinTeam={handleJoinTeam}
                  myCurrentTeam={myCurrentTeam}
                  dragging={dragging}
                  isDropTarget={dragOverTeam === 1}
                  onDropPlayer={handleDropPlayer}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onPlayerDragStart={handlePlayerDragStart}
                  onPlayerDragEnd={handlePlayerDragEnd}
                />

                <CentralStage
                  lobby={lobby}
                  isLeader={isLeader}
                  onModeChange={handleModeChange}
                  onRegionChange={handleRegionChange}
                  onOpenMap={() => setMapOpen(true)}
                  onOpenRules={() => setRulesOpen(true)}
                  onRandomise={handleRandomise}
                  onStart={handleStart}
                  starting={starting}
                />

                <TeamSlab
                  team={2} side="right"
                  lobby={lobby}
                  players={team2Players}
                  bots={team2Bots}
                  maxSize={maxSizePerTeam}
                  canManage={isLeader}
                  isLeader={isLeader}
                  lobbyLeaderId={lobby.leader_id}
                  onRenameTeam={handleRename}
                  onTeamNamesLockToggle={handleNamesLockToggle}
                  onSlotMenu={(t, e) => handleSlotMenu(t, e)}
                  onAddBot={handleAddBot}
                  onJoinTeam={handleJoinTeam}
                  myCurrentTeam={myCurrentTeam}
                  dragging={dragging}
                  isDropTarget={dragOverTeam === 2}
                  onDropPlayer={handleDropPlayer}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onPlayerDragStart={handlePlayerDragStart}
                  onPlayerDragEnd={handlePlayerDragEnd}
                />
              </div>
            </div>
          )}

          {/* Phase 2: Provisioning */}
          {phase === 'provisioning' && (
            <Provisioning lobby={lobby} progress={serverProgress} message={serverMessage} connectReady={connectReady} />
          )}

          {/* Phase 3: In-match */}
          {phase === 'inmatch' && (
            <InMatch lobby={lobby} stats={stats} myUserId={user?.id} connectReady={connectReady} />
          )}

          {/* Phase 4: Match-over */}
          {phase === 'matchover' && (
            <MatchOver
              lobby={lobby}
              stats={stats}
              isLeader={isLeader}
              onPlayAgain={handlePlayAgain}
              onDisband={() => setConfirmDisband(true)}
              working={working}
            />
          )}

          {/* Sheets */}
          <Sheet
            open={mapOpen} onClose={() => setMapOpen(false)}
            title="Choose map" width={1100}
          >
            <MapPicker
              mode={normaliseMode(lobby.mode)}
              current={lobby.selected_map}
              onPick={handleMapPick}
            />
          </Sheet>

          <Sheet
            open={rulesOpen} onClose={() => setRulesOpen(false)}
            title="Match rules" width={620}
          >
            <MatchRulesPanel
              lobby={lobby}
              isLeader={isLeader}
              onKnife={handleKnife}
              onAllTalk={handleAllTalk}
              onTeamLock={handleTeamLock}
            />
          </Sheet>

          <Sheet
            open={inviteOpen} onClose={() => setInviteOpen(false)}
            title="Invite friends" width={520}
          >
            <FriendInviteSheet
              friends={friends}
              cooldowns={inviteCooldowns}
              onInvite={(uname) => handleInviteFriend(uname)}
            />
          </Sheet>

          {/* Confirmations */}
          <Confirm
            open={!!confirmKick}
            title="Kick player"
            body={`Are you sure you want to kick ${confirmKick?.username || 'this player'}? They won't be able to rejoin this lobby.`}
            confirmLabel="Kick"
            danger
            onConfirm={() => handleKickConfirmed(confirmKick)}
            onClose={() => setConfirmKick(null)}
          />
          <Confirm
            open={confirmDisband}
            title="Disband lobby"
            body="This will close the lobby for everyone. This action can't be undone."
            confirmLabel="Disband"
            danger
            onConfirm={handleDisbandConfirmed}
            onClose={() => setConfirmDisband(false)}
          />
          <Confirm
            open={confirmLeave}
            title="Leave lobby"
            body="You'll exit back to the lobby browser. If you're the leader, leadership passes to another player."
            confirmLabel="Leave"
            onConfirm={handleLeaveConfirmed}
            onClose={() => setConfirmLeave(false)}
          />

          {/* Slot menu */}
          {slotMenu && (
            <SlotMenu
              target={slotMenu}
              anchor={slotMenu.anchor}
              onClose={() => setSlotMenu(null)}
              onKick={(u) => { setSlotMenu(null); setConfirmKick(u); }}
              onPromote={handlePromote}
              onRemoveBot={handleRemoveBot}
            />
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%',
          transform: 'translateX(-50%)', zIndex: 400,
          background: C.card,
          borderTop: `1px solid ${C.borderHi}`, borderRight: `1px solid ${C.borderHi}`,
          borderBottom: `2px solid ${C.red}`, borderLeft: `1px solid ${C.borderHi}`,
          padding: '12px 22px',
          fontFamily: F.body, fontSize: 14, color: C.off,
          letterSpacing: '0.05em',
          boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
          animation: 'sheetUp 220ms ease-out',
        }}>{toast}</div>
      )}

      {/* Incoming lobby invite (if any external system feeds setIncomingInvite) */}
      <InvitePopup
        invite={incomingInvite}
        onAccept={() => { if (incomingInvite?.code) doJoinByCode(incomingInvite.code); setIncomingInvite(null); }}
        onDecline={() => setIncomingInvite(null)}
      />
    </div>
  );
}